<template>
  <Loader v-if="loading" />

  <div class="pps-not-found-page">
    <div class="page-toolbar">
      <div>
        <h2>Nuove posa contatore</h2>
        <p>Possibili forniture in RCU associabili a pratiche di nuova posa.</p>
      </div>
      <Button label="Aggiorna" icon="pi pi-refresh" @click="loadPageData" :disabled="loading || loadingConfigurationStatus" />
    </div>

    <div class="summary-grid">
      <div class="summary-item">
        <span>Totale</span>
        <strong>{{ totalSuggestions }}</strong>
      </div>
      <div class="summary-item">
        <span>Associabili</span>
        <strong>{{ associableItems.length }}</strong>
      </div>
      <div class="summary-item">
        <span>Senza coincidenze</span>
        <strong>{{ notAssociableItems.length }}</strong>
      </div>
    </div>

    <Message v-if="loaded && !totalSuggestions" severity="info" :closable="false">
      Non ci sono forniture da analizzare.
    </Message>

    <div v-if="notAssociableItems.length" class="without-suggestions-panel">
      <div class="without-suggestions-header">
        <h3>Punti senza suggerimenti</h3>
        <span>{{ notAssociableItems.length }}</span>
      </div>
      <div class="without-suggestions-list">
        <div
          v-for="(item, index) in notAssociableItems"
          :key="`without-${getSupplyKey(item, index)}`"
          class="without-suggestions-row"
        >
          <strong>{{ getSupplyCode(item) }}</strong>
          <span>#{{ getSupplyId(item) || index + 1 }}</span>
          <span>{{ formatValue(item.supply?.type) }}</span>
          <span>{{ formatValue(item.supply?.data_decorrenza) }}</span>
        </div>
      </div>
    </div>

    <div v-if="associableItems.length" class="cards-list">
      <div v-for="(item, index) in associableItems" :key="getSupplyKey(item, index)" class="card association-card">
        <h4>
          <!-- {{ getSupplyId(item) || index + 1 }} - -->
          <strong>{{ getSupplyCode(item) }}</strong>
        </h4>

        <div class="association-layout">
          <div class="association-column supply-panel">
            <h5>Punto in fornitura senza contratto</h5>
            <div class="supply-detail-block">
              <div class="detail-grid">
                <div v-for="field in getVisibleSupplyFields(item)" :key="field.key" class="detail-cell">
                  <span>{{ formatLabel(field.key) }}</span>
                  <strong>{{ formatValue(field.value) }}</strong>
                </div>
              </div>
            </div>
          </div>

          <div class="association-result">
            <div class="result-box" :class="item.associable ? 'result-ok' : 'result-ko'">
              <i :class="item.associable ? 'pi pi-check' : 'pi pi-times'"></i>
              <span>{{ item.associable ? `${getAllMatches(item).length} candidat${getAllMatches(item).length === 1 ? 'o' : 'i'}` : 'Nessuna coincidenza' }}</span>
            </div>
            <Button
              label="Associa"
              icon="pi pi-link"
              class="p-button-success associate-button"
              :disabled="!canAssociateRows || !getSelectedMatch(item, index)"
              @click="openAssociationDialog(item, index)"
            />
            <small v-if="!canAssociateRows" class="association-config-message">
              {{ associationConfigurationMessage }}
            </small>
          </div>

          <div class="association-column candidates-panel">
            <h5>Pratiche candidate</h5>
            <div v-if="getAllMatches(item).length" class="candidate-list">
              <div
                v-for="(match, matchIndex) in getAllMatches(item)"
                :key="getMatchKey(match, matchIndex)"
                class="candidate-block"
                :class="{ 'candidate-muted': isMatchMuted(item, index, match, matchIndex), 'candidate-selected': isMatchSelected(item, index, match, matchIndex) }"
              >
                <div class="candidate-select-row">
                  <Checkbox
                    :inputId="`${getSupplyKey(item, index)}-${getMatchKey(match, matchIndex)}`"
                    :modelValue="isMatchSelected(item, index, match, matchIndex)"
                    :binary="true"
                    :disabled="isMatchMuted(item, index, match, matchIndex)"
                    @update:modelValue="(checked) => setMatchSelection(checked, item, index, match, matchIndex)"
                  />
                  <label :for="`${getSupplyKey(item, index)}-${getMatchKey(match, matchIndex)}`" class="match-source">
                    {{ getMatchLabel(match.source) }}
                  </label>
                </div>
                <div class="detail-grid candidate-detail-grid">
                  <div v-for="field in getVisibleMatchFields(match.data)" :key="field.key" class="detail-cell">
                    <span>{{ formatLabel(field.key) }}</span>
                    <strong>{{ formatValue(field.value) }}</strong>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="no-match-block">
              Nessuna pratica candidata trovata
            </div>
          </div>
        </div>
      </div>
    </div>

    <Dialog v-model:visible="associationDialogVisible" header="Associa fornitura" :modal="true" :style="{ width: '82vw' }">
      <div v-if="associationContext" class="association-dialog">
        <div class="dialog-summary">
          <div>
            <span>Fornitura</span>
            <strong>{{ associationForm.supply.POD_PDR }}</strong>
          </div>
          <div>
            <span>Candidato</span>
            <strong>{{ getMatchLabel(associationContext.match.source) }}</strong>
          </div>
          <div>
            <span>Tipo</span>
            <strong>{{ associationForm.supply.TIPO }}</strong>
          </div>
        </div>

        <Message v-if="Object.keys(associationErrors).length" severity="warn" :closable="false" class="mb-3">
          Compila i dati evidenziati per simulare il salvataggio multisito.
        </Message>

        <div class="association-wizard">
          <div class="wizard-steps">
            <button
              v-for="(step, stepIndex) in associationWizardSteps"
              :key="step.key"
              type="button"
              class="wizard-step-button"
              :class="{ active: associationActiveStep === stepIndex, complete: canCompleteAssociationStep(step.key) }"
              :disabled="!canOpenAssociationStep(stepIndex)"
              @click="openAssociationStep(stepIndex)"
            >
              <span>{{ stepIndex + 1 }}</span>
              {{ step.label }}
            </button>
          </div>

          <div v-if="activeAssociationStepKey === 'contract'" class="association-step-panel box-option">
            <h3 class="m-2 col-12 flex justify-content-center">
              <strong>Contratto</strong>
            </h3>
            <div class="grid p-fluid">
              <div class="col-12 xl:col-4 field">
                <label>Agente</label>
                <Dropdown
                  v-model="associationForm.agente"
                  :options="agents"
                  optionLabel="name"
                  optionValue="id"
                  :filter="true"
                  :loading="loadingAgents"
                  placeholder="Seleziona un agente"
                  :class="{ 'p-invalid': associationErrors.agente }"
                  @change="onAssociationAgentChange"
                />
              </div>
              <div class="col-12 xl:col-4 field">
                <label>Tipo fornitura</label>
                <InputText :value="associationTypeMatrice.toUpperCase()" disabled />
              </div>
              <div class="col-12 xl:col-4 field">
                <label>Pratica</label>
                <InputText value="Nuova Posa Contatore" disabled />
              </div>
            </div>
          </div>

          <div v-if="activeAssociationStepKey === 'client'" class="association-step-panel box-option">
            <h3 class="m-2 col-12 flex justify-content-center">
              <strong>{{ associationForm.typeClient === 2 ? 'Cliente e contatto' : 'Cliente' }}</strong>
            </h3>
            <div class="grid p-fluid">
              <div class="col-12">
                <label class="m-2 col-12 flex justify-content-center">
                  <strong>Tipologia cliente</strong>
                </label>
                <div class="type-client-selector" :class="{ 'type-client-selector-invalid': associationErrors.typeClient }">
                  <Button
                    v-for="tipo in typeClientOptions"
                    :key="tipo.id"
                    :label="(tipo.name || '').replaceAll('/', ' ').toUpperCase()"
                    :class="`card-primary btn-tipo-cliente btn-selection ${associationForm.typeClient === tipo.id ? 'selected' : ''}`"
                    @click="onAssociationTypeClientChange(tipo.id)"
                  />
                </div>
              </div>
              <template v-if="associationForm.typeClient">
                <div class="col-12 section-divider">
                  <strong>Dati cliente</strong>
                </div>
                <div v-if="associationForm.typeClient !== 2" class="col-12 md:col-3 field">
                  <label>Ragione sociale</label>
                  <InputText v-model="associationForm.cliente.ragsoc" :class="{ 'p-invalid': associationErrors.clienteName }" />
                </div>
                <div v-if="associationForm.typeClient === 2" class="col-12 md:col-3 field">
                  <label>Nome</label>
                  <InputText v-model="associationForm.cliente.nome" :class="{ 'p-invalid': associationErrors.clienteName }" />
                </div>
                <div v-if="associationForm.typeClient === 2" class="col-12 md:col-3 field">
                  <label>Cognome</label>
                  <InputText v-model="associationForm.cliente.cognome" :class="{ 'p-invalid': associationErrors.clienteName }" />
                </div>
                <div class="col-12 md:col-3 field">
                  <label>Codice fiscale</label>
                  <InputMask v-model="associationForm.cliente.codfis" :mask="clienteCodfisMask" :key="clienteCodfisMask" class="text_toupper" :class="{ 'p-invalid': associationErrors.clienteCodice || associationErrors.clienteCodfis }" />
                </div>
                <div v-if="associationForm.typeClient !== 2" class="col-12 md:col-3 field">
                  <label>Partita IVA</label>
                  <InputMask v-model="associationForm.cliente.piva" mask="99999999999" class="text_toupper" :class="{ 'p-invalid': associationErrors.clienteCodice || associationErrors.clientePiva }" />
                </div>
                <div class="col-12 md:col-3 field">
                  <label>Codice SDI</label>
                  <Dropdown
                    v-model="associationForm.cliente.codiceDestinatario"
                    :options="associationSdiOptions"
                    optionLabel="sdi"
                    optionValue="sdi"
                    :filter="true"
                    placeholder="Seleziona"
                    :class="{ 'p-invalid': associationErrors.clienteSdi }"
                  />
                </div>
                <div class="col-12 md:col-3 field">
                  <label>Email</label>
                  <InputText v-model="associationForm.cliente.email" :class="{ 'p-invalid': associationErrors.clienteEmail }" />
                </div>
                <div class="col-12 md:col-3 field">
                  <label>Cellulare</label>
                  <InputMask v-model="associationForm.cliente.cellulare" mask="999999999?9" :unmask="true" slotChar="" :class="{ 'p-invalid': associationErrors.clienteCellulare }" />
                </div>
                <div class="col-12 md:col-3 field">
                  <label>Toponimo cliente</label>
                  <Dropdown v-model="associationForm.cliente.toponimo" :options="toponimi" optionLabel="name" optionValue="name" :filter="true" placeholder="Seleziona toponimo" :class="{ 'p-invalid': associationErrors.clienteToponimo }" />
                </div>
                <div class="col-12 md:col-3 field">
                  <label>Indirizzo cliente</label>
                  <InputText v-model="associationForm.cliente.indirizzo" :class="{ 'p-invalid': associationErrors.clienteIndirizzo }" />
                </div>
                <div class="col-12 md:col-2 field">
                  <label>Civico cliente</label>
                  <InputText v-model="associationForm.cliente.civico" :class="{ 'p-invalid': associationErrors.clienteCivico }" />
                </div>
                <div class="col-12 md:col-2 field">
                  <label>CAP cliente</label>
                  <InputMask v-model="associationForm.cliente.cap" mask="99999" :class="{ 'p-invalid': associationErrors.clienteCap }" />
                </div>
                <div class="col-12 md:col-3 field">
                  <label>Provincia cliente</label>
                  <Dropdown
                    v-model="associationForm.cliente.provincia"
                    :options="province"
                    optionLabel="name"
                    optionValue="code"
                    :filter="true"
                    placeholder="Seleziona provincia"
                    :class="{ 'p-invalid': associationErrors.clienteProvincia }"
                    @change="(event) => onAssociationProvinceChange(event, 'cliente')"
                  />
                </div>
                <div class="col-12 md:col-3 field">
                  <label>Comune cliente</label>
                  <Dropdown
                    v-model="associationForm.cliente.cod_comune"
                    :options="citiesCliente"
                    optionLabel="name"
                    optionValue="id"
                    :filter="true"
                    placeholder="Seleziona comune"
                    :class="{ 'p-invalid': associationErrors.clienteComune }"
                    @change="(event) => onAssociationCityChange(event, 'cliente')"
                  />
                </div>
                <div v-if="associationForm.typeClient === 2" class="col-12 field">
                  <Button label="Copia dati cliente su contatto" icon="pi pi-copy" class="p-button-secondary p-button-sm" @click="copyClienteToContatto" />
                </div>
                <div v-if="associationForm.typeClient === 2" class="col-12 section-divider">
                  <strong>Contatto</strong>
                </div>
                <div v-if="associationForm.typeClient === 2" class="col-12 md:col-3 field">
                  <label>Email contatto</label>
                  <InputText v-model="associationForm.contatto.email" :class="{ 'p-invalid': associationErrors.contattoEmail }" />
                </div>
                <div v-if="associationForm.typeClient === 2" class="col-12 md:col-3 field">
                  <label>Cellulare contatto</label>
                  <InputMask v-model="associationForm.contatto.cellulare" mask="999999999?9" :unmask="true" slotChar="" :class="{ 'p-invalid': associationErrors.contattoCellulare }" />
                </div>
                <div v-if="associationForm.typeClient === 2" class="col-12 md:col-3 field">
                  <label>Toponimo contatto</label>
                  <Dropdown v-model="associationForm.contatto.toponimo" :options="toponimi" optionLabel="name" optionValue="name" :filter="true" placeholder="Seleziona toponimo" :class="{ 'p-invalid': associationErrors.contattoToponimo }" />
                </div>
                <div v-if="associationForm.typeClient === 2" class="col-12 md:col-3 field">
                  <label>Indirizzo contatto</label>
                  <InputText v-model="associationForm.contatto.indirizzo" :class="{ 'p-invalid': associationErrors.contattoIndirizzo }" />
                </div>
                <div v-if="associationForm.typeClient === 2" class="col-12 md:col-2 field">
                  <label>Civico contatto</label>
                  <InputText v-model="associationForm.contatto.civico" :class="{ 'p-invalid': associationErrors.contattoCivico }" />
                </div>
                <div v-if="associationForm.typeClient === 2" class="col-12 md:col-2 field">
                  <label>CAP contatto</label>
                  <InputMask v-model="associationForm.contatto.cap" mask="99999" :unmask="true" slotChar="" :class="{ 'p-invalid': associationErrors.contattoCap }" />
                </div>
                <div v-if="associationForm.typeClient === 2" class="col-12 md:col-3 field">
                  <label>Provincia contatto</label>
                  <Dropdown
                    v-model="associationForm.contatto.provincia"
                    :options="province"
                    optionLabel="name"
                    optionValue="code"
                    :filter="true"
                    placeholder="Seleziona provincia"
                    :class="{ 'p-invalid': associationErrors.contattoProvincia }"
                    @change="(event) => onAssociationProvinceChange(event, 'contatto')"
                  />
                </div>
                <div v-if="associationForm.typeClient === 2" class="col-12 md:col-3 field">
                  <label>Comune contatto</label>
                  <Dropdown
                    v-model="associationForm.contatto.cod_comune"
                    :options="citiesContatto"
                    optionLabel="name"
                    optionValue="id"
                    :filter="true"
                    placeholder="Seleziona comune"
                    :class="{ 'p-invalid': associationErrors.contattoComune }"
                    @change="(event) => onAssociationCityChange(event, 'contatto')"
                  />
                </div>
              </template>
            </div>
          </div>

          <div v-if="activeAssociationStepKey === 'rappresentante'" class="association-step-panel box-option">
            <h3 class="m-2 col-12 flex justify-content-center">
              <strong>Rappresentante</strong>
            </h3>
            <div class="grid p-fluid">
              <div class="col-12 field rappresentante-type-selector">
                <div v-for="forma in rappresentanteFormeGiuridiche" :key="forma.key" class="flex align-items-center mr-4">
                  <RadioButton v-model="selectedRappresentanteFormaGiuridica" :inputId="`rapp-forma-${forma.key}`" :value="forma.key" @change="onRappresentanteFormaGiuridicaChange" />
                  <label :for="`rapp-forma-${forma.key}`" class="ml-2">{{ forma.value }}</label>
                </div>
              </div>
              <div class="col-12 rappresentante-search-row">
                <div class="field rappresentante-search-field">
                  <label>Codice fiscale / Partita IVA rappresentante</label>
                  <InputMask
                    v-model="associationForm.rappresentante.codfis"
                    :mask="rappresentanteCodfisMask"
                    :key="rappresentanteCodfisMask"
                    class="text_toupper"
                    :class="{ 'p-invalid': associationErrors.rappresentanteCodfis }"
                    :disabled="loadingRappresentante"
                    @update:modelValue="onRappresentanteCodfisInput"
                  />
                  <small v-if="loadingRappresentante" class="association-loading-text">
                    <i class="pi pi-spin pi-spinner mr-1"></i>
                    Ricerca rappresentante in corso...
                  </small>
                </div>
              </div>
              <div v-if="associationForm.rappresentante.exist === true" class="col-12">
                <div class="rappresentante-found-box">
                  <h4>{{ getAssociationRappresentanteDisplayName() }}</h4>
                  <span>Codice fiscale / Partita IVA: <strong>{{ associationForm.rappresentante.codfis }}</strong></span>
                </div>
              </div>
              <div v-if="associationForm.rappresentante.exist === false" class="col-12 rappresentante-details-row">
                <template v-if="selectedRappresentanteFormaGiuridica === 0">
                  <div class="field rappresentante-detail-field">
                    <label>Nome</label>
                    <InputText v-model="associationForm.rappresentante.nome" :disabled="associationForm.rappresentante.exist" :class="{ 'p-invalid': associationErrors.rappresentanteName }" />
                  </div>
                  <div class="field rappresentante-detail-field">
                    <label>Cognome</label>
                    <InputText v-model="associationForm.rappresentante.cognome" :disabled="associationForm.rappresentante.exist" :class="{ 'p-invalid': associationErrors.rappresentanteName }" />
                  </div>
                </template>
                <div v-else class="field rappresentante-detail-field rappresentante-detail-field-wide">
                  <label>Ragione sociale</label>
                  <InputText v-model="associationForm.rappresentante.name" :disabled="associationForm.rappresentante.exist" :class="{ 'p-invalid': associationErrors.rappresentanteName }" />
                </div>
              </div>
              <div v-if="associationForm.rappresentante.exist === false" class="col-12">
                <InlineMessage severity="info">
                  Se il codice fiscale non viene trovato, il rappresentante verra creato con i dati inseriti.
                </InlineMessage>
              </div>
              <div v-if="needsRappresentanteContactInput" class="col-12 section-divider">
                <strong>Contatto rappresentante</strong>
              </div>
              <div v-if="needsRappresentanteContactInput" class="col-12 md:col-3 field">
                <label>Email contatto</label>
                <InputText v-model="associationForm.contatto.email" :class="{ 'p-invalid': associationErrors.contattoEmail }" />
              </div>
              <div v-if="needsRappresentanteContactInput" class="col-12 md:col-3 field">
                <div class="flex justify-content-between align-items-center">
                  <label>PEC <span v-if="requiresRappresentantePec && !(canUseAssociationPecBypass && associationForm.contatto.bypass_pec.value)" class="p-error">(obbligatoria)</span></label>
                  <div v-if="canUseAssociationPecBypass" class="flex align-items-center">
                    <Checkbox inputId="association-bypass-pec" v-model="associationForm.contatto.bypass_pec.value" :binary="true" @update:modelValue="onAssociationBypassPecChange" />
                    <label for="association-bypass-pec" class="ml-2 mb-0">Bypass PEC</label>
                  </div>
                </div>
                <InputText v-model="associationForm.contatto.pec" :disabled="canUseAssociationPecBypass && associationForm.contatto.bypass_pec.value" :class="{ 'p-invalid': associationErrors.contattoPec }" />
                <div v-if="canUseAssociationPecBypass && associationForm.contatto.bypass_pec.value" class="mt-2">
                  <label>Motivo del bypass</label>
                  <Textarea v-model="associationForm.contatto.bypass_pec.reason" rows="2" class="w-full" :class="{ 'p-invalid': associationErrors.contattoBypassPecReason }" />
                </div>
              </div>
              <div v-if="needsRappresentanteContactInput" class="col-12 md:col-3 field">
                <label>Cellulare contatto</label>
                <InputMask v-model="associationForm.contatto.cellulare" mask="999999999?9" :unmask="true" slotChar="" :class="{ 'p-invalid': associationErrors.contattoCellulare }" />
              </div>
              <div v-if="needsRappresentanteContactInput" class="col-12 md:col-3 field">
                <label>Toponimo contatto</label>
                <Dropdown v-model="associationForm.contatto.toponimo" :options="toponimi" optionLabel="name" optionValue="name" :filter="true" placeholder="Seleziona toponimo" :class="{ 'p-invalid': associationErrors.contattoToponimo }" />
              </div>
              <div v-if="needsRappresentanteContactInput" class="col-12 md:col-3 field">
                <label>Indirizzo contatto</label>
                <InputText v-model="associationForm.contatto.indirizzo" :class="{ 'p-invalid': associationErrors.contattoIndirizzo }" />
              </div>
              <div v-if="needsRappresentanteContactInput" class="col-12 md:col-2 field">
                <label>Civico contatto</label>
                <InputText v-model="associationForm.contatto.civico" :class="{ 'p-invalid': associationErrors.contattoCivico }" />
              </div>
              <div v-if="needsRappresentanteContactInput" class="col-12 md:col-2 field">
                <label>CAP contatto</label>
                <InputMask v-model="associationForm.contatto.cap" mask="99999" :unmask="true" slotChar="" :class="{ 'p-invalid': associationErrors.contattoCap }" />
              </div>
              <div v-if="needsRappresentanteContactInput" class="col-12 md:col-3 field">
                <label>Provincia contatto</label>
                <Dropdown
                  v-model="associationForm.contatto.provincia"
                  :options="province"
                  optionLabel="name"
                  optionValue="code"
                  :filter="true"
                  placeholder="Seleziona provincia"
                    :class="{ 'p-invalid': associationErrors.contattoProvincia }"
                  @change="(event) => onAssociationProvinceChange(event, 'contatto')"
                />
              </div>
              <div v-if="needsRappresentanteContactInput" class="col-12 md:col-3 field">
                <label>Comune contatto</label>
                <Dropdown
                  v-model="associationForm.contatto.cod_comune"
                  :options="citiesContatto"
                  optionLabel="name"
                  optionValue="id"
                  :filter="true"
                  placeholder="Seleziona comune"
                  :class="{ 'p-invalid': associationErrors.contattoComune }"
                  @change="(event) => onAssociationCityChange(event, 'contatto')"
                />
              </div>
            </div>
          </div>

          <div v-if="activeAssociationStepKey === 'supply'" class="association-step-panel box-option">
            <h3 class="m-2 col-12 flex justify-content-center">
              <strong>Dati fornitura</strong>
            </h3>
            <div class="grid p-fluid">
              <div class="col-12 md:col-3 field">
                <label>POD/PDR</label>
                <InputText v-model="associationForm.supply.POD_PDR" disabled />
              </div>
              <div class="col-12 md:col-3 field">
                <label>Offerta</label>
                <Dropdown
                  v-model="associationForm.supply.OFFERTA"
                  :options="associationOfferteOptions"
                  optionLabel="name"
                  optionValue="id"
                  :filter="true"
                  :loading="loadingOfferte"
                  :class="{ 'p-invalid': associationErrors.offerta }"
                />
              </div>
              <div class="col-12 md:col-3 field">
                <label>Uso</label>
                <Dropdown
                  v-model="associationForm.supply.USO"
                  :options="associationUsoOptions"
                  optionLabel="name"
                  optionValue="name"
                  :class="{ 'p-invalid': associationErrors.uso }"
                />
              </div>
              <div class="col-12 md:col-3 field">
                <label>Data inizio</label>
                <Calendar v-model="associationForm.supply.DATA_DECORRENZA" dateFormat="dd/mm/yy" :manualInput="false" showIcon :class="{ 'p-invalid': associationErrors.dataDecorrenza }" @update:modelValue="normalizeAssociationDates" />
              </div>
              <div class="col-12 md:col-3 field">
                <label>IBAN</label>
                <InputMask class="text_toupper" mask="IT99a9999999999****99999*99" placeholder="Inserisci IBAN" v-model="associationForm.supply.IBAN" @update:modelValue="onAssociationIbanInput" />
              </div>
              <div v-if="hasAssociationIban" class="col-12 md:col-3 field checkbox-field">
                <Checkbox v-model="associationForm.supply.checked_data_iban_equal_to_client" :binary="true" />
                <label>IBAN intestato al cliente</label>
              </div>
              <div v-if="hasAssociationIban && !associationForm.supply.checked_data_iban_equal_to_client" class="col-12 md:col-3 field">
                <label>Intestatario IBAN</label>
                <InputText v-model="associationForm.supply.INTESTAZIONE_IBAN" :class="{ 'p-invalid': associationErrors.ibanOwner }" />
              </div>
              <div v-if="hasAssociationIban && !associationForm.supply.checked_data_iban_equal_to_client" class="col-12 md:col-3 field">
                <label>CF/PIVA intestatario IBAN</label>
                <InputText v-model="associationForm.supply.CODICE_FISCALE_PIVA_IBAN" :class="{ 'p-invalid': associationErrors.ibanOwner }" />
              </div>
              <div class="col-12 md:col-3 field">
                <label>Toponimo fornitura</label>
                <InputText v-model="associationForm.supply.TOPONIMO_FORNITURA" :class="{ 'p-invalid': associationErrors.supplyAddress }" />
              </div>
              <div class="col-12 md:col-3 field">
                <label>Indirizzo fornitura</label>
                <InputText v-model="associationForm.supply.INDIRIZZO_FORNITURA" :class="{ 'p-invalid': associationErrors.supplyAddress }" />
              </div>
              <div class="col-12 md:col-2 field">
                <label>Civico</label>
                <InputText v-model="associationForm.supply.CIVICO_FORNITURA" />
              </div>
              <div class="col-12 md:col-2 field">
                <label>CAP</label>
                <InputText v-model="associationForm.supply.CAP_FORNITURA" :class="{ 'p-invalid': associationErrors.supplyAddress }" />
              </div>
              <div class="col-12 md:col-3 field">
                <label>Provincia</label>
                <Dropdown
                  v-model="associationForm.supply.SIGLA_PROVINCIA_FORNITURA"
                  :options="province"
                  optionLabel="name"
                  optionValue="code"
                  :filter="true"
                  placeholder="Seleziona provincia"
                  :class="{ 'p-invalid': associationErrors.supplyAddress }"
                  @change="(event) => onAssociationProvinceChange(event, 'supply')"
                />
              </div>
              <div class="col-12 md:col-3 field">
                <label>Comune fornitura</label>
                <Dropdown
                  v-model="associationForm.supply.ISTAT_FORNITURA"
                  :options="citiesFornitura"
                  optionLabel="name"
                  optionValue="id"
                  :filter="true"
                  placeholder="Seleziona comune"
                  :class="{ 'p-invalid': associationErrors.supplyComune }"
                  @change="(event) => onAssociationCityChange(event, 'supply')"
                />
              </div>
              <div v-if="associationForm.supply.TIPO === 'pdr'" class="col-12 md:col-3 field">
                <label>Matricola</label>
                <InputText v-model="associationForm.supply.MATRICOLA" :class="{ 'p-invalid': associationErrors.gasMatricola }" />
              </div>
              <div v-if="associationForm.supply.TIPO === 'pdr'" class="col-12 md:col-3 field">
                <label>Classe misuratore</label>
                <Dropdown
                  v-model="associationForm.supply.CLASSE_MISURATORE"
                  :options="classeGasOptions"
                  optionLabel="classe"
                  dataKey="id"
                  :class="{ 'p-invalid': associationErrors.gasClasse }"
                />
              </div>
              <div v-if="associationForm.supply.TIPO === 'pdr'" class="col-12 md:col-3 field">
                <label>Destinazione d'uso</label>
                <Dropdown
                  v-model="associationForm.supply.DESTINAZIONE_USO"
                  :options="categorieGasOptions"
                  optionLabel="name"
                  dataKey="id"
                  :class="{ 'p-invalid': associationErrors.gasDestinazione }"
                />
              </div>
              <div v-if="associationForm.supply.TIPO === 'pdr'" class="col-12 md:col-3 field">
                <label>Consumo annuo</label>
                <InputText v-model="associationForm.supply.CONSUMO_ANNUO" :class="{ 'p-invalid': associationErrors.gasConsumo }" />
              </div>
            </div>
          </div>

          <div v-if="activeAssociationStepKey === 'documents'" class="association-step-panel document-section">
            <div class="association-document-block flex flex-column">
              <h3 class="m-2 col-12 flex justify-content-center">
                <strong>Opzioni contratto</strong>
              </h3>
              <div class="w-full flex flex-wrap justify-content-between">
                <div class="col-12 md:col-5 xl:col-2 text-center association-option-card" v-if="canSelectSpedizione">
                  <label class="m-3"><strong>Tipologia di spedizione</strong></label>
                  <div v-for="spedizione in availableSpedizioni" :key="spedizione.key ?? spedizione.id ?? spedizione.name" class="col-12 flex flex-column p-0">
                    <div class="flex m-2">
                      <RadioButton v-model="associationForm.spedizione" :inputId="`association-spedizione-${spedizione.key ?? spedizione.id ?? spedizione.name}`" :value="spedizione.name" :disabled="isAssociationSpedizioneDisabled(spedizione)" />
                      <label :for="`association-spedizione-${spedizione.key ?? spedizione.id ?? spedizione.name}`" :class="['ml-2', { 'text-color-secondary': isAssociationSpedizioneDisabled(spedizione) }]">{{ spedizione.name }}</label>
                    </div>
                  </div>
                </div>
                <div class="col-12 md:col-5 xl:col-2 text-center association-option-card" v-if="canSelectBimestrale">
                  <label class="m-3"><strong>Frequenza di fatturazione</strong></label>
                  <div v-for="frequenza in availableFrequenze" :key="frequenza.name" class="col-12 flex flex-column p-0">
                    <div class="flex m-2">
                      <RadioButton v-model="associationForm.frequenza" :inputId="`association-frequenza-${frequenza.name}`" :value="frequenza.name" />
                      <label :for="`association-frequenza-${frequenza.name}`" class="ml-2">{{ frequenza.name }}</label>
                    </div>
                  </div>
                </div>
                <div class="col-12 md:col-5 xl:col-2 text-center association-option-card" v-if="canSelectIva">
                  <label class="m-3"><strong>Iva Agevolata Energia</strong></label>
                  <div class="m-3">
                    <Checkbox v-model="associationForm.iva" :binary="true" />
                  </div>
                </div>
                <div class="col-12 md:col-5 xl:col-2 text-center association-option-card" v-if="canSelectDataSwitch">
                  <label class="m-3"><strong>Data switch</strong></label>
                  <div class="m-3 flex justify-content-center">
                    <Calendar v-model="associationForm.data_switch" dateFormat="dd/mm/yy" :manualInput="false" :maxDate="associationDataSwitchMaxDate" showIcon :class="{ 'p-invalid': associationErrors.dataSwitch }" />
                  </div>
                </div>
                <div class="col-12 md:col-5 xl:col-2 text-center association-option-card">
                  <label class="m-3"><strong>Tipologia firma</strong></label>
                  <div class="m-3 association-fixed-option">
                    <i class="pi pi-check-circle mr-2"></i>
                    <span>Contratto cartaceo</span>
                  </div>
                  <div class="mt-3 grid justify-content-center">
                    <div class="flex align-items-center">
                      <label class="w-full">Data firma:</label>
                      <Calendar v-model="associationForm.data_firma" dateFormat="dd/mm/yy" :manualInput="false" :maxDate="associationDataFirmaMaxDate" class="m-2" :class="{ 'p-invalid': associationErrors.dataFirma }" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Message v-if="loadingRequiredFields" severity="info" :closable="false">
              Verifica documenti e opzioni in corso.
            </Message>

            <div class="association-document-block association-documents-box p-2">
              <h3 class="m-2 col-12 flex justify-content-center">
                <strong>Documenti</strong>
              </h3>
              <div v-if="requiredDocuments.length === 0" class="m-2 col-12 flex flex-column align-items-center">
                Nessun documento necessario. Procedi alla simulazione del salvataggio.
              </div>
              <div v-else class="m-2 col-12 flex flex-column align-items-center">
                Documenti necessari:
                <ul class="association-required-documents">
                  <li v-for="document in requiredDocuments" :key="`label-${document.ref}`"><strong>{{ document.name }}</strong></li>
                </ul>
                <small>Formati consentiti: application/pdf, image/gif, image/jpeg, image/jpg, image/png</small>
              </div>
              <div v-if="requiredDocuments.length > 0" class="flex flex-wrap justify-content-center">
                <div v-for="document in requiredDocuments" :key="document.ref" class="col-12 sm:col-6 md:col-4 lg:col-3 xl:col-2 flex justify-content-center">
                  <div class="flex flex-column align-items-center mb-3 justify-content-center" :class="{ 'p-invalid-card': associationErrors[document.ref] || associationErrors[`type_${document.ref}`] }">
                    <div class="button-wrapper mb-2">
                      <span class="label flex align-items-center justify-content-center">Carica {{ document.name }}</span>
                      <input class="upload-file" :name="document.ref" type="file" accept="application/pdf,image/gif,image/jpeg,image/jpg,image/png" :multiple="document.multiple" @change="handleAssociationFileUpload($event, document)" />
                    </div>
                    <div>
                      <p class="text-center">File selezionati:</p>
                      <ul>
                        <li v-for="file in document.files" :key="file.name">{{ file.name }}</li>
                      </ul>
                    </div>
                    <div v-show="associationErrors[document.ref]" class="p-error">Campo richiesto.</div>
                    <div v-show="associationErrors[`type_${document.ref}`]" class="p-error">Formato non consentito</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="association-document-block flex flex-column align-items-center" v-if="canInsertNote">
              <h3 class="m-2 col-12 flex justify-content-center">
                <strong>Note</strong>
              </h3>
              <Textarea style="width: 95%;" v-model="associationForm.note" rows="5" cols="30" class="m-2" />
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <Button label="Annulla" icon="pi pi-times" class="p-button-text" @click="associationDialogVisible = false" />
        <Button label="Indietro" icon="pi pi-arrow-left" class="p-button-secondary" :disabled="associationActiveStep === 0" @click="previousAssociationStep" />
        <Button v-if="!isLastAssociationStep" label="Avanti" icon="pi pi-arrow-right" iconPos="right" @click="nextAssociationStep" />
        <Button v-else label="Associa nuova posa" icon="pi pi-save" class="p-button-success" :disabled="savingAssociation" @click="submitAssociation" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue'
import moment from 'moment'
import Loader from '@/components/Loader.vue'
import Service from '@/services/ask-pratiche/pratiche-wizard'
import Opzioni from '@/config/opzioni'
import { useStore } from 'vuex'

const emptyAssociationForm = () => ({
  matrice_id: '',
  agente: '',
  typeClient: 0,
  clientSuggestionSource: '',
  pratica: 'np',
  iva: false,
  spedizione: 'Mail',
  contratto_manuale: true,
  data_switch: new Date(),
  data_firma: null,
  data_creazione_pratica: null,
  id_promozione: '',
  id_cliente_promozione: '',
  frequenza: 'Mensile',
  note: '',
  cliente: {
    id: '',
    ragsoc: '',
    nome: '',
    cognome: '',
    codfis: '',
    piva: '',
    codiceDestinatario: '',
    toponimo: '',
    indirizzo: '',
    civico: '',
    cap: '',
    provincia: '',
    cod_comune: '',
    nome_comune: '',
    email: '',
    pec: '',
    cellulare: ''
  },
  contatto: {
    id: '',
    toponimo: '',
    indirizzo: '',
    civico: '',
    cap: '',
    provincia: '',
    cod_comune: '',
    nome_comune: '',
    email: '',
    pec: '',
    bypass_pec: {
      value: false,
      reason: ''
    },
    cellulare: ''
  },
  rappresentante: {
    id: 0,
    exist: null,
    codfis: '',
    nome: '',
    cognome: ''
  },
  supply: {
    TIPO: '',
    RAGIONE_SOCIALE: '',
    CODICE_FISCALE_PIVA: '',
    CODICE_SDI: '0000000',
    IBAN: '',
    POD_PDR: '',
    TOPONIMO_FORNITURA: '',
    INDIRIZZO_FORNITURA: '',
    CIVICO_FORNITURA: '',
    CAP_FORNITURA: '',
    SIGLA_PROVINCIA_FORNITURA: '',
    CITTA_FORNITURA: '',
    USO: '',
    OFFERTA: '',
    CF_ATTIVAZIONE: '',
    PIVA_ATTIVAZIONE: '',
    TIPO_VOLTURA: 'Ordinaria',
    INTESTAZIONE_IBAN: '',
    CODICE_FISCALE_PIVA_IBAN: '',
    checked_data_iban_equal_to_client: true,
    PRECHECKOK: true,
    DATA_DECORRENZA: new Date(),
    DATA_DECORRENZA_FORMATTED: '',
    CLASSE_MISURATORE: null,
    DESTINAZIONE_USO: null,
    POTENZA_TERMICA: ''
  }
})

export default {
  components: {
    Loader
  },
  setup() {
    const store = useStore()
    const self = getCurrentInstance()
    self.$eventBus = self.appContext.config.globalProperties.$eventBus
    const service = new Service(self.$eventBus)
    const opzioni = new Opzioni()
    const loading = ref(false)
    const currentUser = computed(() => store.state.auth.user)
    const isAdmin = computed(() => currentUser.value?.role === 'admin')
    const loaded = ref(false)
    const loadingConfigurationStatus = ref(false)
    const configurationStatus = ref({
      ready: false,
      message: 'Non ci sono tutte le configurazioni necessarie'
    })
    const items = ref([])
    const itemsWithoutSuggestions = ref([])
    const totals = ref({})
    const sdiOptions = ref([])
    const province = ref(opzioni.province)
    const toponimi = ref(opzioni.toponimi)
    const citiesCliente = ref([])
    const citiesContatto = ref([])
    const citiesFornitura = ref([])
    const selectedMatches = ref({})
    const associationDialogVisible = ref(false)
    const associationContext = ref(null)
    const associationForm = ref(emptyAssociationForm())
    const supportsPecBypass = ref(false)
    const canUseAssociationPecBypass = computed(() => isAdmin.value && supportsPecBypass.value)
    const requiresRappresentantePec = computed(() => [1, 3, 4].includes(Number(associationForm.value.typeClient)))
    const needsRappresentanteContactInput = computed(() => {
      const rappresentante = associationForm.value.rappresentante
      return rappresentante?.exist === false || (rappresentante?.exist === true && !associationForm.value.contatto?.id)
    })
    const associationErrors = ref({})
    const savingAssociation = ref(false)
    const associationActiveStep = ref(0)
    const agents = ref([])
    const offerte = ref([])
    const loadingAgents = ref(false)
    const loadingOfferte = ref(false)
    const loadingRequiredFields = ref(false)
    const loadingRappresentante = ref(false)
    const lastSearchedRappresentanteCodfis = ref('')
    const rappresentanteFormeGiuridiche = ref([
      { key: 0, value: 'Persona fisica' },
      { key: 1, value: 'Persona giuridica' }
    ])
    const selectedRappresentanteFormaGiuridica = ref(0)
    const typeClientOptions = ref(opzioni.tipologieCliente)
    const classeGasOptions = ref(opzioni.classiGas)
    const categorieGasOptions = ref(opzioni.categorieGas)
    const requiredDocuments = ref([])
    const canSelectBimestrale = ref(false)
    const canSelectSpedizione = ref(true)
    const canSelectIva = ref(true)
    const canSelectDataSwitch = ref(true)
    const canInsertNote = ref(true)
    const defaultSpedizioni = [
      { id: 1, name: 'Mail + Cartaceo' },
      { id: 2, name: 'Mail' },
      { id: 3, name: 'Cartaceo' }
    ]
    const availableSpedizioni = ref([...defaultSpedizioni])
    const availableFrequenze = ref([{ name: 'Mensile' }])
    const rappresentanteCodfisMask = computed(() => selectedRappresentanteFormaGiuridica.value === 1 ? '99999999999' : 'aaaaaa99a99a9**a')
    const clienteCodfisMask = computed(() => [1, 5, 6].includes(Number(associationForm.value.typeClient)) ? '99999999999' : 'aaaaaa99a99a9**a')
    const allowedDocumentTypes = ['application/pdf', 'image/gif', 'image/jpeg', 'image/jpg', 'image/png']
    const usiEnergia = ref([
      { id: 1, name: 'Illuminazione Pubblica' },
      { id: 2, name: 'Altri Usi' },
      { id: 3, name: 'Domestico residente' },
      { id: 4, name: 'Domestico non residente' }
    ])
    const usiGas = ref([
      { id: 2, name: 'Altri Usi' },
      { id: 4, name: 'Domestico' }
    ])
    const possibleUsiEnergiaForTypeClient = [
      { typeClient: 1, id_usi: [2] },
      { typeClient: 2, id_usi: [2, 3, 4] },
      { typeClient: 3, id_usi: [2] },
      { typeClient: 4, id_usi: [2] },
      { typeClient: 5, id_usi: [1, 2] },
      { typeClient: 6, id_usi: [1, 2] }
    ]
    const possibleUsiGasForTypeClient = [
      { typeClient: 1, id_usi: [2] },
      { typeClient: 2, id_usi: [4] },
      { typeClient: 3, id_usi: [2] },
      { typeClient: 4, id_usi: [2] },
      { typeClient: 5, id_usi: [2] },
      { typeClient: 6, id_usi: [2] }
    ]

    const associableItems = computed(() => items.value.filter((item) => item.associable))
    const notAssociableItems = computed(() => itemsWithoutSuggestions.value.length ? itemsWithoutSuggestions.value : items.value.filter((item) => !item.associable))
    const totalSuggestions = computed(() => totals.value.all ?? (associableItems.value.length + notAssociableItems.value.length))
    const canAssociateRows = computed(() => configurationStatus.value.ready === true)
    const associationConfigurationMessage = computed(() => loadingConfigurationStatus.value ? 'Verifica configurazioni in corso...' : (configurationStatus.value.message || 'Non ci sono tutte le configurazioni necessarie'))
    const associationSdiOptions = computed(() => {
      const currentSdi = associationForm.value.cliente.codiceDestinatario
      const options = normalizeList(sdiOptions.value)

      if (currentSdi && !options.some((option) => option.sdi === currentSdi)) {
        return [{ sdi: currentSdi }, ...options]
      }

      return options
    })
    const associationSupplyType = computed(() => associationForm.value.supply.TIPO)
    const hasAssociationIban = computed(() => Boolean(String(associationForm.value.supply.IBAN || '').trim()))
    const associationTypeMatrice = computed(() => associationSupplyType.value === 'pod' ? 'ee' : 'gas')
    const associationOfferteOptions = computed(() => offerte.value.filter((offerta) => associationSupplyType.value === 'pod' ? offerta.tipologia === 1 : offerta.tipologia !== 1))
    const associationUsoOptions = computed(() => {
      const source = associationSupplyType.value === 'pod' ? usiEnergia.value : usiGas.value
      const rules = associationSupplyType.value === 'pod' ? possibleUsiEnergiaForTypeClient : possibleUsiGasForTypeClient
      const allowedIds = (rules.find((rule) => rule.typeClient === associationForm.value.typeClient) || { id_usi: [] }).id_usi
      return source.filter((uso) => allowedIds.includes(uso.id))
    })
    const needsAssociationRappresentante = computed(() => associationForm.value.typeClient && associationForm.value.typeClient !== 2)
    const associationWizardSteps = computed(() => {
      const steps = [
        { key: 'contract', label: 'Contratto' },
        { key: 'client', label: 'Cliente' }
      ]

      if (needsAssociationRappresentante.value) {
        steps.push({ key: 'rappresentante', label: 'Rappresentante' })
      }

      steps.push(
        { key: 'supply', label: 'Fornitura' },
        { key: 'documents', label: 'Documenti' }
      )

      return steps
    })
    const activeAssociationStepKey = computed(() => associationWizardSteps.value[associationActiveStep.value]?.key || 'contract')
    const isLastAssociationStep = computed(() => associationActiveStep.value === associationWizardSteps.value.length - 1)

    const normalizeList = (list) => {
      return Array.isArray(list) ? list : []
    }

    const normalizeItem = (item) => {
      const supply = item.supply || item.pps_not_found_in_rcu || item.pp || item
      const directMatches = normalizeList(item.direct_matches || item.pratiche || item.pratica_matches)
      const noteMatches = normalizeList(item.note_matches || item.posa_contatore_matches || item.pratica_posa_contatore_matches)

      return {
        supply,
        rcu_data: item.rcu_data || item.rcuData || {},
        client_suggestions: item.client_suggestions || item.clientSuggestions || {},
        direct_matches: directMatches,
        note_matches: noteMatches,
        associable: item.associable ?? (directMatches.length > 0 || noteMatches.length > 0)
      }
    }

    const loadSuggestions = async () => {
      loading.value = true
      await service.getPpsNotFoundRcuSuggestions()
        .then((res) => {
          const payload = res.items || {}
          const responseItems = payload.items_with_suggestions || payload.items?.items || payload.items || payload.data || payload || []
          const responseWithoutSuggestions = payload.items_without_suggestions || payload.without_suggestions || payload.not_associable_items || []
          items.value = normalizeList(responseItems).map(normalizeItem)
          itemsWithoutSuggestions.value = normalizeList(responseWithoutSuggestions).map(normalizeItem)
          totals.value = payload.totals || {}
          selectedMatches.value = {}
          selectSingleCandidateMatches()
          loaded.value = true
        })
        .finally(() => {
          loading.value = false
        })
    }

    const loadConfigurationStatus = async () => {
      loadingConfigurationStatus.value = true
      await service.getPpsNotFoundRcuConfigurationStatus()
        .then((res) => {
          const payload = res.items || {}
          configurationStatus.value = {
            ready: Boolean(payload.ready),
            message: payload.message || 'Non ci sono tutte le configurazioni necessarie'
          }
        })
        .finally(() => {
          loadingConfigurationStatus.value = false
        })
    }

    const loadPageData = async () => {
      await Promise.all([
        loadConfigurationStatus(),
        loadSuggestions()
      ])
    }

    const loadSdi = async () => {
      await service.getSDI()
        .then((res) => {
          sdiOptions.value = normalizeList(res.items)
        })
    }

    const syncAssociationSdiOptions = async () => {
      await loadSdi()

      if ([2, 3].includes(Number(associationForm.value.typeClient))) {
        associationForm.value.cliente.codiceDestinatario = '0000000'
      }
    }

    const loadAgents = async () => {
      loadingAgents.value = true
      await service.getAgents()
        .then((res) => {
          agents.value = normalizeList(res.items)
        })
        .finally(() => {
          loadingAgents.value = false
        })
    }

    const getSupplyCode = (item) => item.supply?.code || item.code || ''
    const getSupplyId = (item) => item.supply?.id || item.id
    const getSupplyKey = (item, index) => `${getSupplyId(item) || index}-${getSupplyCode(item)}`

    const getRowFields = (row) => {
      return Object.entries(row || {})
        .filter(([, value]) => !Array.isArray(value) && (value === null || typeof value !== 'object'))
        .map(([key, value]) => ({ key, value }))
    }

    const hasDisplayValue = (value) => {
      return value !== null && value !== undefined && value !== ''
    }

    const getPrioritizedFields = (row, priority, maxFallbackFields = 6) => {
      const entries = Object.entries(row || {})
        .filter(([, value]) => !Array.isArray(value) && typeof value !== 'object' && hasDisplayValue(value))
      const keys = entries.map(([key]) => key)
      const visibleKeys = [
        ...priority.filter((key) => keys.includes(key)),
        ...keys.filter((key) => !priority.includes(key)).slice(0, maxFallbackFields)
      ]

      return visibleKeys.map((key) => ({ key, value: row[key] }))
    }

    const formatCfPiva = (cf, piva) => {
      const parts = []
      if (cf) parts.push(`CF ${cf}`)
      if (piva) parts.push(`PIVA ${piva}`)
      return parts.join(' - ')
    }
    const getRcuCustomerSource = (item) => {
      const supplyType = String(item?.supply?.type || item?.type || getSupplyTypeFromCode(getSupplyCode(item))).toLowerCase()
      return supplyType === 'ee' || supplyType === 'pod'
        ? item?.rcu_data?.energia_df
        : item?.rcu_data?.gas
    }

    const getVisibleSupplyFields = (item) => {
      const supply = item?.supply || item || {}
      const rcuCustomer = getRcuCustomerSource(item)
      const name = [
        getObjectValue(rcuCustomer, 'Nome'),
        getObjectValue(rcuCustomer, 'Cognome')
      ].filter(Boolean).join(' ').trim()

      return getPrioritizedFields({
        code: supply.code,
        cliente_rcu: name || getObjectValue(rcuCustomer, 'Ragione_Sociale_Denominazione', 'Ragione_sociale_denominazione'),
        cf_piva_rcu: formatCfPiva(
          getObjectValue(rcuCustomer, 'Cf', 'CF'),
          getObjectValue(rcuCustomer, 'Piva', 'PIVA')
        ),
        data_decorrenza: supply.data_decorrenza
      }, [
        'code',
        'cliente_rcu',
        'cf_piva_rcu',
        'data_decorrenza'
      ], 0)
    }

    const getAllMatches = (item) => {
      return [
        ...item.direct_matches.map((match) => ({ source: match.match_source || 'posa_contatore_code', data: match })),
        ...item.note_matches.map((match) => ({ source: match.match_source || 'posa_contatore_note', data: match }))
      ]
    }

    const getMatchKey = (match, index) => {
      const data = match.data || {}
      return [
        match.source,
        data.id || data.pratica_id || data.stato_pratiche_clienti_id || data.stato_cod_pratica || index
      ].join('-')
    }

    const selectSingleCandidateMatches = () => {
      const nextSelections = {}

      items.value.forEach((item, index) => {
        const matches = getAllMatches(item)
        if (matches.length === 1) {
          nextSelections[getSupplyKey(item, index)] = getMatchKey(matches[0], 0)
        }
      })

      selectedMatches.value = nextSelections
    }

    const getMatchLabel = (source) => {
      return source === 'posa_contatore_code' ? 'ModificaPosaContatore da code' : 'ModificaPosaContatore da nota'
    }

    const getSelection = (item, index) => selectedMatches.value[getSupplyKey(item, index)] || null

    const isMatchSelected = (item, index, match, matchIndex) => {
      return getSelection(item, index) === getMatchKey(match, matchIndex)
    }

    const isMatchMuted = (item, index, match, matchIndex) => {
      const selection = getSelection(item, index)
      return Boolean(selection) && selection !== getMatchKey(match, matchIndex)
    }

    const setMatchSelection = (checked, item, index, match, matchIndex) => {
      const supplyKey = getSupplyKey(item, index)
      const nextSelections = { ...selectedMatches.value }

      if (checked) {
        nextSelections[supplyKey] = getMatchKey(match, matchIndex)
      } else {
        delete nextSelections[supplyKey]
      }

      selectedMatches.value = nextSelections
    }

    const getSelectedMatch = (item, index) => {
      const selection = getSelection(item, index)
      if (!selection) return null

      return getAllMatches(item).find((match, matchIndex) => getMatchKey(match, matchIndex) === selection) || null
    }

    const getColumns = (rows) => {
      const priority = ['id', 'code', 'cod_type_stato_pratica', 'type_stato_pratica', 'pratica_id', 'stato_cod_pratica', 'stato_type_file', 'stato_nota', 'nota']
      const keys = [...new Set(rows.flatMap((row) => Object.keys(row || {})))]
      return [
        ...priority.filter((key) => keys.includes(key)),
        ...keys.filter((key) => !priority.includes(key))
      ]
    }

    const getMatchFields = (row) => {
      const columns = getColumns([row])
      return columns.map((key) => ({ key, value: row[key] }))
    }

    const getCandidateClientePosa = (row) => {
      const nominativo = [
        getObjectValue(row, 'nomerappresentante', 'nomeRappresentante', 'nome_rappresentante', 'NomeRappresentante'),
        getObjectValue(row, 'cognomerappresentante', 'cognomeRappresentante', 'cognome_rappresentante', 'CognomeRappresentante')
      ].filter(Boolean).join(' ').trim()

      return nominativo || getObjectValue(row, 'intestatario')
    }

    const getVisibleMatchFields = (row) => {
      const displayRow = {
        ...row,
        cliente_posa: getCandidateClientePosa(row),
        cf_piva_posa: formatCfPiva(
          getObjectValue(row, 'codice_fiscale', 'codiceFiscale', 'Cf', 'CF'),
          getObjectValue(row, 'partita_iva', 'partitaIva', 'Piva', 'PIVA')
        )
      }

      return getPrioritizedFields(displayRow, [
        'code',
        'cliente_posa',
        'cf_piva_posa',
        'stato_nota',
        'nota',
        'new_type_contratto',
        'stato_cod_pratica',
        'codicePratica'
      ], 0)
    }

    const formatLabel = (value) => {
      const labels = {
        new_type_contratto: 'Tipo contratto',
        stato_cod_pratica: 'Stato pratica',
        stato_nota: 'Nota',
        nota: 'Nota utente',
        codicePratica: 'Codice pratica',
        cliente_posa: 'Cliente pratica',
        cliente_rcu: 'Cliente RCU',
        cf_piva_rcu: 'CF/PIVA',
        cf_piva_posa: 'CF/PIVA',
        code: 'Code',
        data_decorrenza: 'Data decorrenza'
      }

      return labels[value] || String(value).replace(/_/g, ' ')
    }

    const formatValue = (value) => {
      if (value === null || value === undefined || value === '') {
        return '-'
      }

      if (typeof value === 'boolean') {
        return value ? 'Si' : 'No'
      }

      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) && moment(value).isValid()) {
        return moment(value).format(value.includes(':') ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY')
      }

      return value
    }

    const upper = (value) => String(value || '').trim().toUpperCase()

    const getSupplyTypeFromCode = (code) => {
      const normalizedCode = upper(code)
      return normalizedCode.startsWith('IT') ? 'pod' : 'pdr'
    }

    const parseDate = (value) => {
      if (!value) return new Date()
      if (value instanceof Date) return value
      const date = moment(value)
      return date.isValid() ? date.toDate() : new Date()
    }

    const formatDateForPayload = (value) => {
      if (!value) return null
      return moment(value).format('YYYY-MM-DD')
    }

    const associationDataSwitchMaxDate = computed(() => {
      return associationForm.value.supply?.DATA_DECORRENZA
        ? parseDate(associationForm.value.supply.DATA_DECORRENZA)
        : null
    })
    const getAssociationDataFirmaMaxDate = (form, fallbackDate = null) => {
      const value = form?.data_creazione_pratica || fallbackDate || form?.supply?.DATA_DECORRENZA
      return value ? parseDate(value) : null
    }
    const associationDataFirmaMaxDate = computed(() => getAssociationDataFirmaMaxDate(associationForm.value))

    const isAssociationDataSwitchAfterStart = () => {
      if (!associationForm.value.data_switch || !associationDataSwitchMaxDate.value) return false

      return moment(associationForm.value.data_switch).startOf('day').isAfter(moment(associationDataSwitchMaxDate.value).startOf('day'))
    }
    const isAssociationDataFirmaAfterStart = () => {
      if (!associationForm.value.data_firma || !associationDataFirmaMaxDate.value) return false

      return moment(associationForm.value.data_firma).startOf('day').isAfter(moment(associationDataFirmaMaxDate.value).startOf('day'))
    }

    const normalizeAssociationDataSwitch = () => {
      if (isAssociationDataSwitchAfterStart()) {
        associationForm.value.data_switch = parseDate(associationDataSwitchMaxDate.value)
      }
    }
    const normalizeAssociationDataFirma = () => {
      if (isAssociationDataFirmaAfterStart()) {
        associationForm.value.data_firma = parseDate(associationDataFirmaMaxDate.value)
      }
    }
    const normalizeAssociationDates = () => {
      normalizeAssociationDataSwitch()
      normalizeAssociationDataFirma()
      clearAssociationErrors(['dataDecorrenza', 'dataSwitch', 'dataFirma'])
    }
    const getFirstValue = (...values) => {
      return values.find((value) => value !== undefined && value !== null && value !== '') || ''
    }

    const normalizeLookup = (value) => {
      return String(value || '').trim().toLowerCase()
    }

    const findGasClasse = (value) => {
      if (value && typeof value === 'object') return value

      const lookup = normalizeLookup(value)
      if (!lookup) return null

      return classeGasOptions.value.find((option) => {
        return normalizeLookup(option.id) === lookup || normalizeLookup(option.classe) === lookup
      }) || null
    }

    const getDefaultGasClasse = () => {
      return classeGasOptions.value.find((option) => option.classe === 'G4') || classeGasOptions.value[0] || null
    }

    const findGasCategoria = (value) => {
      if (value && typeof value === 'object') return value

      const lookup = normalizeLookup(value)
      if (!lookup) return null

      return categorieGasOptions.value.find((option) => {
        return normalizeLookup(option.id) === lookup || normalizeLookup(option.code) === lookup || normalizeLookup(option.name) === lookup
      }) || null
    }

    const getDefaultGasCategoria = () => {
      return categorieGasOptions.value.find((option) => option.code === 'C3') || categorieGasOptions.value[0] || null
    }

    const mergeFilled = (target, source) => {
      Object.entries(source || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          target[key] = value
        }
      })

      return target
    }

    const getObjectValue = (object, ...keys) => {
      if (!object) return ''

      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(object, key) && object[key] !== undefined && object[key] !== null && object[key] !== '') {
          return object[key]
        }
      }

      const normalizedMap = Object.entries(object).reduce((map, [key, value]) => {
        map[key.toLowerCase()] = value
        return map
      }, {})

      for (const key of keys) {
        const value = normalizedMap[String(key).toLowerCase()]
        if (value !== undefined && value !== null && value !== '') return value
      }

      return ''
    }

    const getClientSuggestion = (item, typeClient) => {
      const suggestions = item?.client_suggestions || item?.clientSuggestions || {}
      return suggestions[typeClient] || suggestions[String(typeClient)] || null
    }

    const findCityOption = (cities, idOrName, name = '') => {
      const normalizedIdOrName = String(idOrName || '').trim().toUpperCase()
      const normalizedName = String(name || '').trim().toUpperCase()

      return cities.find((city) => String(city.id) === String(idOrName)) ||
        cities.find((city) => String(city.cod_istat || '').trim().toUpperCase() === normalizedIdOrName) ||
        cities.find((city) => String(city.name || '').trim().toUpperCase() === normalizedIdOrName) ||
        cities.find((city) => String(city.name || '').trim().toUpperCase() === normalizedName)
    }

    const setAssociationCities = (target, preserveComune = true) => {
      const row = associationForm.value[target]
      const isSupply = target === 'supply'
      const provincia = isSupply ? row.SIGLA_PROVINCIA_FORNITURA : row.provincia
      const cities = provincia ? opzioni.getCities(provincia) : []

      if (target === 'cliente') {
        citiesCliente.value = cities
      } else if (isSupply) {
        citiesFornitura.value = cities
      } else {
        citiesContatto.value = cities
      }

      if (isSupply) {
        if (!preserveComune) {
          row.ISTAT_FORNITURA = null
          row.CITTA_FORNITURA = ''
          return
        }

        const selectedCity = findCityOption(cities, row.ISTAT_FORNITURA, row.CITTA_FORNITURA)
        if (selectedCity) {
          row.ISTAT_FORNITURA = selectedCity.id
          row.CITTA_FORNITURA = selectedCity.name
        }
        return
      }

      if (!preserveComune) {
        row.cod_comune = null
        row.nome_comune = ''
      } else if (row.cod_comune) {
        row.nome_comune = row.nome_comune || opzioni.getNameCityById(row.cod_comune)
      }
    }

    const syncAssociationCities = () => {
      setAssociationCities('cliente')
      setAssociationCities('contatto')
      setAssociationCities('supply')
    }

    const onAssociationProvinceChange = (event, target) => {
      if (target === 'supply') {
        associationForm.value.supply.SIGLA_PROVINCIA_FORNITURA = event.value
      } else {
        associationForm.value[target].provincia = event.value
      }
      setAssociationCities(target, false)
      clearAssociationErrors(target === 'cliente'
        ? ['clienteProvincia', 'clienteComune']
        : target === 'supply'
          ? ['supplyAddress', 'supplyComune']
          : ['contattoProvincia', 'contattoComune'])
    }

    const onAssociationCityChange = (event, target) => {
      if (target === 'supply') {
        associationForm.value.supply.ISTAT_FORNITURA = event.value
        associationForm.value.supply.CITTA_FORNITURA = opzioni.getNameCityById(event.value)
      } else {
        associationForm.value[target].cod_comune = event.value
        associationForm.value[target].nome_comune = opzioni.getNameCityById(event.value)
      }
      clearAssociationErrors(target === 'cliente'
        ? ['clienteProvincia', 'clienteComune']
        : target === 'supply'
          ? ['supplyAddress', 'supplyComune']
          : ['contattoProvincia', 'contattoComune'])
    }

    const normalizeAssociationContact = (contact) => {
      if (!contact) return {}

      const bypassPec = contact.bypass_pec && typeof contact.bypass_pec === 'object'
        ? {
            value: contact.bypass_pec.value === true || Number(contact.bypass_pec.value) === 1,
            reason: contact.bypass_pec.reason || ''
          }
        : {
            value: Number(contact.bypass_pec) === 1,
            reason: contact.motivazione_bypass_pec || ''
          }

      return {
        id: contact.id || '',
        toponimo: contact.toponimo || '',
        indirizzo: contact.indirizzo || '',
        civico: contact.civico || '',
        cap: contact.cap || '',
        provincia: contact.provincia || '',
        cod_comune: contact.cod_comune || contact.comune || '',
        nome_comune: contact.nome_comune || contact.name || '',
        email: contact.email || '',
        pec: contact.pec || '',
        bypass_pec: bypassPec,
        cellulare: contact.cellulare || contact.cell || contact.tel || '',
        telefono: contact.telefono || contact.tel || ''
      }
    }

    const hasAssociationPecBypass = (contact = {}) => {
      if (contact.bypass_pec && typeof contact.bypass_pec === 'object') {
        return contact.bypass_pec.value === true || Number(contact.bypass_pec.value) === 1
      }

      return Number(contact.bypass_pec) === 1
    }

    const hasAssociationContactData = (contact = {}) => {
      const hasEmailAndMobile = Boolean(contact.email && (contact.cell || contact.cellulare || contact.tel))
      if (!hasEmailAndMobile) return false
      if (!requiresRappresentantePec.value) return true

      const hasPec = Boolean(contact.pec && String(contact.pec).trim())
      return hasPec || (canUseAssociationPecBypass.value && hasAssociationPecBypass(contact))
    }

    const clearAssociationClientAndContact = (form, identifiers = {}) => {
      form.cliente = {
        ...emptyAssociationForm().cliente,
        codfis: identifiers.codfis || '',
        piva: identifiers.piva || ''
      }
      form.contatto = emptyAssociationForm().contatto
      form.clientSuggestionSource = ''
      syncAssociationSdiForForm(form)
    }

    const clearAssociationRappresentanteContact = () => {
      associationForm.value.contatto = emptyAssociationForm().contatto
      citiesContatto.value = []
      clearAssociationErrors(['contattoEmail', 'contattoPec', 'contattoBypassPecReason', 'contattoCellulare', 'contattoToponimo', 'contattoIndirizzo', 'contattoCivico', 'contattoCap', 'contattoProvincia', 'contattoComune'])
    }

    const syncAssociationSdiForForm = (form) => {
      if ([2, 3].includes(Number(form.typeClient))) {
        form.cliente.codiceDestinatario = '0000000'
      }
    }

    const hasAddressData = (row, fields) => {
      return fields.some((field) => String(row?.[field] || '').trim() !== '')
    }

    const syncSupplyAddressFromClienteFallback = (form) => {
      const clienteAddressFields = ['toponimo', 'indirizzo', 'civico', 'cap', 'provincia', 'cod_comune', 'nome_comune']
      const supplyAddressFields = ['TOPONIMO_FORNITURA', 'INDIRIZZO_FORNITURA', 'CIVICO_FORNITURA', 'CAP_FORNITURA', 'SIGLA_PROVINCIA_FORNITURA', 'ISTAT_FORNITURA', 'CITTA_FORNITURA']

      if (!form || !hasAddressData(form.cliente, clienteAddressFields)) return

      const hasSupplyAddress = hasAddressData(form.supply, supplyAddressFields)
      const shouldUseClienteAddress = form.supply.TIPO === 'pdr' || !hasSupplyAddress
      if (!shouldUseClienteAddress) return

      form.supply.TOPONIMO_FORNITURA = form.cliente.toponimo || ''
      form.supply.INDIRIZZO_FORNITURA = form.cliente.indirizzo || ''
      form.supply.CIVICO_FORNITURA = form.cliente.civico || ''
      form.supply.CAP_FORNITURA = form.cliente.cap || ''
      form.supply.SIGLA_PROVINCIA_FORNITURA = form.cliente.provincia || ''
      form.supply.CITTA_FORNITURA = form.cliente.nome_comune || ''
      form.supply.ISTAT_FORNITURA = form.cliente.cod_comune || ''
      if (form === associationForm.value) setAssociationCities('supply')
    }

    const applyAssociationClientSuggestion = (form, item) => {
      const suggestion = getClientSuggestion(item, form.typeClient)
      form.clientSuggestionSource = ''

      if (!suggestion || (!suggestion.exists && !suggestion.source)) {
        syncAssociationSdiForForm(form)
        syncSupplyAddressFromClienteFallback(form)
        return
      }

      form.clientSuggestionSource = suggestion.source || ''

      if (suggestion.cliente) {
        mergeFilled(form.cliente, suggestion.cliente)
      }

      if (suggestion.contatto) {
        mergeFilled(form.contatto, normalizeAssociationContact(suggestion.contatto))
      }

      if (suggestion.rappresentante) {
        form.rappresentante = {
          ...getEmptyRappresentante(),
          ...form.rappresentante,
          ...suggestion.rappresentante
        }
      }

      syncAssociationSdiForForm(form)
      syncAssociationCities()
      syncSupplyAddressFromClienteFallback(form)
    }

    const getAssociationClientLookupData = (form) => {
      const datas = {
        cf: upper(form.cliente.codfis)
      }

      if ([1, 4, 5, 6].includes(Number(form.typeClient))) {
        datas.piva = upper(form.cliente.piva)
      }

      return datas
    }

    const canCheckAssociationClient = (datas) => {
      return Object.values(datas).every((value) => value !== null && value !== undefined && String(value).trim() !== '')
    }

    const setAssociationContactFromList = (contacts) => {
      const contact = normalizeList(contacts).find(hasAssociationContactData)
      if (!contact) return

      mergeFilled(associationForm.value.contatto, normalizeAssociationContact(contact))
      syncAssociationCities()
    }

    const loadAssociationContact = async (id, type) => {
      if (!id) return

      await service.getContacts(id, type)
        .then((res) => {
          setAssociationContactFromList(res.items)
        })
    }

    const setAssociationRappresentanteFromClient = (cliente) => {
      if (!cliente?.cod_rapp) return

      const nameParts = String(cliente.name_rapp || '').trim().split(/\s+/).filter(Boolean)
      const nome = nameParts.shift() || ''

      associationForm.value.rappresentante = {
        ...getEmptyRappresentante(),
        id: cliente.cod_rapp,
        exist: true,
        codfis: cliente.codfis_rapp || '',
        nome,
        cognome: nameParts.join(' ')
      }
    }

    const applyExistingAssociationClient = async (data) => {
      const cliente = data.cliente || {}
      associationForm.value.clientSuggestionSource = 'client'
      mergeFilled(associationForm.value.cliente, cliente)
      associationForm.value.cliente.nome_comune = cliente.nome_comune || opzioni.getNameCityById(cliente.cod_comune)
      syncAssociationSdiForForm(associationForm.value)
      syncAssociationCities()
      syncSupplyAddressFromClienteFallback(associationForm.value)

      if (Number(associationForm.value.typeClient) === 2) {
        setAssociationContactFromList(data.contatti)
        if (!associationForm.value.contatto.id) {
          await loadAssociationContact(associationForm.value.cliente.id, 'cliente')
        }
      } else {
        ensureAssociationRappresentante()
        setAssociationRappresentanteFromClient(cliente)
        await loadAssociationContact(cliente.cod_rapp, 'rappresentante')
      }
    }

    const syncAssociationClientFromBackend = async () => {
      const form = associationForm.value
      const lookupData = getAssociationClientLookupData(form)
      const identifiers = {
        codfis: lookupData.cf || form.cliente.codfis,
        piva: lookupData.piva || form.cliente.piva
      }

      clearAssociationClientAndContact(form, identifiers)

      if (!canCheckAssociationClient(lookupData)) {
        applyAssociationClientSuggestion(form, associationContext.value?.item)
        return
      }

      await service.check_exist_client({
        type_client: form.typeClient,
        datas: lookupData
      }).then(async (res) => {
        const data = res.res?.data || {}

        if (data.exist) {
          await applyExistingAssociationClient(data)
        } else {
          applyAssociationClientSuggestion(form, associationContext.value?.item)
        }
      })
    }

    const getRcuSources = (item, supplyType) => {
      const rcuData = item.rcu_data || {}

      if (supplyType === 'pdr') {
        return {
          client: rcuData.gas || {},
          address: rcuData.gas || {},
          tech: rcuData.gas || {}
        }
      }

      return {
        client: rcuData.energia_df || {},
        address: rcuData.energia_ind || {},
        tech: rcuData.energia_dt || {}
      }
    }

    const normalizeRcuUso = (value, supplyType, typeClient) => {
      const normalized = String(value || '').trim().toLowerCase()

      if (!normalized) return ''
      if (supplyType === 'pdr') return typeClient === 2 ? 'Domestico' : 'Altri Usi'
      if (normalized.includes('domest')) return typeClient === 2 ? 'Domestico residente' : 'Altri Usi'
      if (normalized.includes('illuminazione')) return 'Illuminazione Pubblica'

      return 'Altri Usi'
    }

    const inferTypeClientFromRcu = (rcu) => {
      const piva = getObjectValue(rcu, 'Piva', 'PIVA', 'Af_piva')
      const ragioneSociale = getObjectValue(rcu, 'Ragione_Sociale_Denominazione', 'Ragione_sociale_denominazione', 'Af_ragione_sociale_denominazione')
      const nome = getObjectValue(rcu, 'Nome', 'Af_nome')
      const cognome = getObjectValue(rcu, 'Cognome', 'Af_cognome')
      const settore = String(getObjectValue(rcu, 'Sett_Merceologico', 'Sett_merceologico') || '').trim()
      const ragione = String(ragioneSociale || '').toLowerCase()
      const hasPerson = Boolean(nome || cognome)

      if (ragione.includes('condominio') || ragione.includes('cond.')) return 3
      if (ragione.includes('comune') || ragione.includes('provincia') || ragione.includes('regione') || ragione.includes('ministero') || settore.startsWith('84')) return 5
      if (ragione.includes('parrocchia') || ragione.includes('consorzio') || ragione.includes('fondazione') || ragione.includes('associazione')) return 6
      if (piva && hasPerson) return 4
      if (!piva && hasPerson && !ragioneSociale) return 2
      if (piva || ragioneSociale) return 1

      return 0
    }

    const getEmptyRappresentante = () => ({
      id: 0,
      exist: null,
      codfis: '',
      name: '',
      nome: '',
      cognome: ''
    })

    const ensureAssociationRappresentante = () => {
      if (!associationForm.value.rappresentante) {
        associationForm.value.rappresentante = getEmptyRappresentante()
      }
    }

    const normalizeRappresentanteCodfis = (value) => upper(String(value || '').replace(/[^a-zA-Z0-9]/g, ''))
    const isValidRappresentanteCodfis = (value) => {
      const normalized = normalizeRappresentanteCodfis(value)
      return selectedRappresentanteFormaGiuridica.value === 1 ? /^\d{11}$/.test(normalized) : /^[A-Z0-9]{16}$/.test(normalized)
    }

    const syncRappresentanteFormaFromCodfis = (value) => {
      const normalized = normalizeRappresentanteCodfis(value)
      if (/^\d{11}$/.test(normalized)) selectedRappresentanteFormaGiuridica.value = 1
      if (/^[A-Z0-9]{16}$/.test(normalized)) selectedRappresentanteFormaGiuridica.value = 0
    }

    const getAssociationRappresentanteDisplayName = () => {
      const rappresentante = associationForm.value.rappresentante || {}
      return rappresentante.name || [rappresentante.nome, rappresentante.cognome].filter(Boolean).join(' ') || 'Rappresentante trovato'
    }

    const onRappresentanteCodfisInput = (value) => {
      ensureAssociationRappresentante()
      const normalized = normalizeRappresentanteCodfis(value)
      if (isValidRappresentanteCodfis(normalized) && lastSearchedRappresentanteCodfis.value === normalized) return

      associationForm.value.rappresentante.exist = null
      clearAssociationErrors(['rappresentanteCodfis'])
      if (isValidRappresentanteCodfis(normalized)) {
        associationForm.value.rappresentante.codfis = normalized
        searchAssociationRappresentante()
      } else {
        lastSearchedRappresentanteCodfis.value = ''
      }
    }

    const onRappresentanteFormaGiuridicaChange = () => {
      ensureAssociationRappresentante()
      associationForm.value.rappresentante = {
        ...getEmptyRappresentante(),
        codfis: '',
        exist: null
      }
      clearAssociationRappresentanteContact()
      lastSearchedRappresentanteCodfis.value = ''
      clearAssociationErrors(['rappresentanteCodfis', 'rappresentanteName'])
    }

    const associationStepErrorKeys = {
      contract: ['agente'],
      client: ['typeClient', 'clienteCodice', 'clienteCodfis', 'clientePiva', 'clienteName', 'clienteSdi', 'clienteEmail', 'clienteCellulare', 'clienteToponimo', 'clienteIndirizzo', 'clienteCivico', 'clienteCap', 'clienteProvincia', 'clienteComune', 'contattoEmail', 'contattoCellulare', 'contattoToponimo', 'contattoIndirizzo', 'contattoCivico', 'contattoCap', 'contattoProvincia', 'contattoComune'],
      rappresentante: ['rappresentanteCodfis', 'rappresentanteName', 'contattoEmail', 'contattoPec', 'contattoBypassPecReason', 'contattoCellulare', 'contattoToponimo', 'contattoIndirizzo', 'contattoCivico', 'contattoCap', 'contattoProvincia', 'contattoComune'],
      supply: ['offerta', 'uso', 'dataDecorrenza', 'supplyAddress', 'supplyComune', 'ibanOwner', 'gasMatricola', 'gasClasse', 'gasDestinazione', 'gasConsumo'],
      documents: ['dataSwitch', 'dataFirma']
    }

    const clearAssociationErrors = (keys) => {
      const nextErrors = { ...associationErrors.value }
      keys.forEach((key) => {
        delete nextErrors[key]
      })
      associationErrors.value = nextErrors
    }

    const isValidAssociationEmail = (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())
    const isValidAssociationMobile = (value) => /^\d{9,10}$/.test(String(value || '').replace(/\D/g, ''))
    const isValidAssociationPiva = (value) => /^\d{11}$/.test(String(value || '').replace(/\D/g, ''))

    const getAssociationStepErrors = (stepKey) => {
      const errors = {}
      const form = associationForm.value
      const cliente = form.cliente
      const contatto = form.contatto || {}
      const supply = form.supply

      if (stepKey === 'contract') {
        if (!form.agente) errors.agente = true
      }

      if (stepKey === 'client') {
        if (!form.typeClient) errors.typeClient = true
        if ([1, 4, 5, 6].includes(Number(form.typeClient)) && !isValidAssociationPiva(cliente.piva)) errors.clientePiva = true
        if ([2, 3].includes(Number(form.typeClient)) && !cliente.codfis) errors.clienteCodfis = true
        if (![1, 2, 3, 4, 5, 6].includes(Number(form.typeClient)) && !cliente.codfis && !cliente.piva) errors.clienteCodice = true
        if (form.typeClient === 2 && (!cliente.nome || !cliente.cognome)) errors.clienteName = true
        if (form.typeClient !== 2 && !cliente.ragsoc) errors.clienteName = true
        if (!cliente.codiceDestinatario) errors.clienteSdi = true
        if (!cliente.email || !isValidAssociationEmail(cliente.email)) errors.clienteEmail = true
        if (!cliente.cellulare || !isValidAssociationMobile(cliente.cellulare)) errors.clienteCellulare = true
        if (!cliente.toponimo) errors.clienteToponimo = true
        if (!cliente.indirizzo) errors.clienteIndirizzo = true
        if (!cliente.civico) errors.clienteCivico = true
        if (!cliente.cap) errors.clienteCap = true
        if (!cliente.provincia) errors.clienteProvincia = true
        if (!cliente.cod_comune) errors.clienteComune = true
        if (form.typeClient === 2) {
          if (!contatto.email || !isValidAssociationEmail(contatto.email)) errors.contattoEmail = true
          if (!contatto.cellulare || !isValidAssociationMobile(contatto.cellulare)) errors.contattoCellulare = true
          if (!contatto.toponimo) errors.contattoToponimo = true
          if (!contatto.indirizzo) errors.contattoIndirizzo = true
          if (!contatto.civico) errors.contattoCivico = true
          if (!contatto.cap) errors.contattoCap = true
          if (!contatto.provincia) errors.contattoProvincia = true
          if (!contatto.cod_comune) errors.contattoComune = true
        }
      }

      if (stepKey === 'rappresentante') {
        ensureAssociationRappresentante()
        if (!form.rappresentante.codfis || !isValidRappresentanteCodfis(form.rappresentante.codfis)) errors.rappresentanteCodfis = true
        if (form.rappresentante.exist === null) errors.rappresentanteCodfis = true
        if (form.rappresentante.exist === false) {
          if (selectedRappresentanteFormaGiuridica.value === 1) {
            if (!form.rappresentante.name) errors.rappresentanteName = true
          } else if (!form.rappresentante.nome || !form.rappresentante.cognome) {
            errors.rappresentanteName = true
          }
        }
        if (needsRappresentanteContactInput.value) {
          if (!contatto.email || !isValidAssociationEmail(contatto.email)) errors.contattoEmail = true
          if (requiresRappresentantePec.value) {
            const bypassPec = canUseAssociationPecBypass.value && contatto.bypass_pec?.value
            if (bypassPec) {
              if (!String(contatto.bypass_pec?.reason || '').trim()) errors.contattoBypassPecReason = true
            } else if (!contatto.pec || !isValidAssociationEmail(contatto.pec)) {
              errors.contattoPec = true
            }
          } else if (contatto.pec && !isValidAssociationEmail(contatto.pec)) {
            errors.contattoPec = true
          }
          if (!contatto.cellulare || !isValidAssociationMobile(contatto.cellulare)) errors.contattoCellulare = true
          if (!contatto.toponimo) errors.contattoToponimo = true
          if (!contatto.indirizzo) errors.contattoIndirizzo = true
          if (!contatto.civico) errors.contattoCivico = true
          if (!contatto.cap) errors.contattoCap = true
          if (!contatto.provincia) errors.contattoProvincia = true
          if (!contatto.cod_comune) errors.contattoComune = true
        }
      }

      if (stepKey === 'supply') {
        if (!supply.OFFERTA) errors.offerta = true
        if (!supply.USO) errors.uso = true
        if (!supply.DATA_DECORRENZA) errors.dataDecorrenza = true
        if (!supply.TOPONIMO_FORNITURA || !supply.INDIRIZZO_FORNITURA || !supply.CAP_FORNITURA) errors.supplyAddress = true
        if (!supply.CITTA_FORNITURA) errors.supplyComune = true
        if (supply.TIPO === 'pdr') {
          if (!supply.MATRICOLA) errors.gasMatricola = true
          if (!supply.CLASSE_MISURATORE) errors.gasClasse = true
          if (!supply.DESTINAZIONE_USO) errors.gasDestinazione = true
          if (!supply.CONSUMO_ANNUO) errors.gasConsumo = true
        }
        if (supply.IBAN && !supply.checked_data_iban_equal_to_client && (!supply.INTESTAZIONE_IBAN || !supply.CODICE_FISCALE_PIVA_IBAN)) errors.ibanOwner = true
      }

      if (stepKey === 'documents') {
        if (canSelectDataSwitch.value && (!form.data_switch || isAssociationDataSwitchAfterStart())) errors.dataSwitch = true
        if (!form.data_firma || isAssociationDataFirmaAfterStart()) errors.dataFirma = true
        requiredDocuments.value.forEach((document) => {
          if (!document.files.length) errors[document.ref] = true
          if (document.files.some((file) => !allowedDocumentTypes.includes(file.type))) errors[`type_${document.ref}`] = true
        })
      }

      return errors
    }

    const canCompleteAssociationStep = (stepKey) => Object.keys(getAssociationStepErrors(stepKey)).length === 0

    const canOpenAssociationStep = (stepIndex) => {
      return associationWizardSteps.value
        .slice(0, stepIndex)
        .every((step) => canCompleteAssociationStep(step.key))
    }

    const openAssociationStep = (stepIndex) => {
      if (!canOpenAssociationStep(stepIndex)) return
      associationActiveStep.value = stepIndex
      if (activeAssociationStepKey.value === 'documents') syncAssociationRequiredFields()
    }

    const validateCurrentAssociationStep = () => {
      const stepKey = activeAssociationStepKey.value
      const errors = getAssociationStepErrors(stepKey)
      clearAssociationErrors(associationStepErrorKeys[stepKey] || [])
      associationErrors.value = { ...associationErrors.value, ...errors }
      return Object.keys(errors).length === 0
    }

    const syncVisibleAssociationErrors = () => {
      const stepKey = activeAssociationStepKey.value
      const stepKeys = associationStepErrorKeys[stepKey] || []
      const hasVisibleStepErrors = stepKeys.some((key) => associationErrors.value[key])
      if (!hasVisibleStepErrors) return

      const errors = getAssociationStepErrors(stepKey)
      const nextErrors = { ...associationErrors.value }
      stepKeys.forEach((key) => {
        delete nextErrors[key]
      })
      associationErrors.value = { ...nextErrors, ...errors }
    }

    const nextAssociationStep = () => {
      if (!validateCurrentAssociationStep()) return

      if (!isLastAssociationStep.value) {
        associationActiveStep.value += 1
      }

      if (activeAssociationStepKey.value === 'supply') syncSupplyAddressFromClienteFallback(associationForm.value)
      if (activeAssociationStepKey.value === 'documents') syncAssociationRequiredFields()
    }

    const previousAssociationStep = () => {
      if (associationActiveStep.value > 0) associationActiveStep.value -= 1
    }

    const getPraticaPosaContatoreId = (match) => {
      return getFirstValue(match?.data?.id, match?.data?.codicePratica, match?.data?.stato_cod_pratica, match?.data?.pratica_id)
    }

    const getPraticaPosaContatoreDbId = (match) => {
      if (!String(match?.source || '').startsWith('posa_contatore')) return ''

      return getFirstValue(match?.data?.id, match?.data?.pratica_posa_contatore_id, match?.data?.stato_pratica_posa_contatore_id)
    }

    const getPraticaPosaContatoreCreationDate = (match) => {
      if (!String(match?.source || '').startsWith('posa_contatore')) return null

      return getFirstValue(match?.data?.data_creazione, match?.data?.created_at, match?.data?.data)
    }

    const buildAssociationForm = (item, match) => {
    console.log(item, match)
      const form = emptyAssociationForm()
      const supply = item.supply || {}
      const candidate = match?.data || {}
      const code = upper(getSupplyCode(item))
      const supplyType = getSupplyTypeFromCode(code)
      const rcuSources = getRcuSources(item, supplyType)
      const rcuClient = rcuSources.client
      const rcuAddress = rcuSources.address
      const rcuTech = rcuSources.tech
      const dataDecorrenza = parseDate(supply.data_decorrenza)

      form.agente = getFirstValue(candidate.agente_posa_contatore_id, candidate.agente_id, candidate.cod_agente, candidate.agente, supply.agente_id, supply.cod_agente)
      form.typeClient = inferTypeClientFromRcu(rcuClient)
      form.pratica = 'np'
      form.contratto_manuale = true
      const dataCreazionePratica = getPraticaPosaContatoreCreationDate(match)
      form.data_creazione_pratica = dataCreazionePratica ? parseDate(dataCreazionePratica) : null
      const dataFirmaMax = getAssociationDataFirmaMaxDate(form, dataDecorrenza)
      form.data_firma = moment().startOf('day').isAfter(moment(dataFirmaMax).startOf('day')) ? parseDate(dataFirmaMax) : new Date()
      form.data_switch = dataDecorrenza
      form.note = `Associazione ${code} (non trovato in RCU) con pratica Nuova posa contatore ${getPraticaPosaContatoreId(match) || '-'}`
      form.cliente = {
        ...form.cliente,
        ragsoc: getObjectValue(rcuClient, 'Ragione_Sociale_Denominazione', 'Ragione_sociale_denominazione', 'Af_ragione_sociale_denominazione'),
        nome: getObjectValue(rcuClient, 'Nome', 'Af_nome'),
        cognome: getObjectValue(rcuClient, 'Cognome', 'Af_cognome'),
        codfis: getObjectValue(rcuClient, 'Cf', 'CF', 'Af_cf'),
        piva: getObjectValue(rcuClient, 'Piva', 'PIVA', 'Af_piva'),
        codiceDestinatario: getObjectValue(rcuClient, 'Codice_Ufficio', 'Codice_ufficio'),
        email: getObjectValue(rcuClient, 'Email_Cliente', 'Email', 'Se_email'),
        cellulare: getObjectValue(rcuClient, 'Telefono_Cliente', 'Telefono', 'Se_telefono'),
        codiceAteco: getObjectValue(rcuClient, 'Sett_Merceologico', 'Sett_merceologico')
      }
      form.contatto = {
        ...form.contatto,
        email: getFirstValue(
          getObjectValue(rcuClient, 'Email_Referente', 'Ref_email'),
          form.cliente.email
        ),
        cellulare: getFirstValue(
          getObjectValue(rcuClient, 'Ref_telefono'),
          form.cliente.cellulare
        )
      }
      form.supply = {
        ...form.supply,
        TIPO: supplyType,
        POD_PDR: code,
        TOPONIMO_FORNITURA: getObjectValue(rcuAddress, 'Toponimo_Forn', 'Toponimo'),
        INDIRIZZO_FORNITURA: getObjectValue(rcuAddress, 'Via_Forn', 'Via', 'Indirizzo_Fornitura', 'Indirizzo'),
        CIVICO_FORNITURA: getObjectValue(rcuAddress, 'Civ_Forn', 'Civ', 'Civico_Fornitura', 'Civico'),
        CAP_FORNITURA: getObjectValue(rcuAddress, 'Cap_Forn', 'Cap', 'Cap_Fornitura'),
        SIGLA_PROVINCIA_FORNITURA: getObjectValue(rcuAddress, 'Prov_Forn', 'Prov', 'Provincia_Fornitura'),
        CITTA_FORNITURA: getObjectValue(rcuAddress, 'Localita_Forn', 'Localita', 'Comune_Fornitura'),
        ISTAT_FORNITURA: getObjectValue(rcuAddress, 'Istat_Forn', 'Istat'),
        USO: getFirstValue(
          supply.uso,
          supply.tipologia_fornitura,
          normalizeRcuUso(getObjectValue(rcuClient, 'Residenza', 'Cat_uso', 'Tipo_fornitura', 'Sett_Merceologico', 'Sett_merceologico'), supplyType, form.typeClient),
          candidate.uso,
          candidate.tipologia_fornitura
        ),
        OFFERTA: getFirstValue(supply.offerta, supply.cod_listino_contratto, candidate.cod_listino_contratto),
        DATA_DECORRENZA: dataDecorrenza,
        DISALIMENTABILE: getFirstValue(getObjectValue(rcuTech, 'Disalimentabilita'), 'Si'),
        POTENZA_IMPEGNATA: getObjectValue(rcuTech, 'Potcontrimp', 'Pot_max_ric'),
        POTENZA_DISPONIBILE: getObjectValue(rcuTech, 'Potdisp'),
        TENSIONE: getObjectValue(rcuTech, 'Tensione'),
        USO_ENERGIA: 'Ordinaria'
      }

      if (supplyType === 'pdr') {
        const gasClasse = findGasClasse(getFirstValue(
          supply.classe_misuratore_fornitura,
          supply.classe,
          getObjectValue(rcuTech, 'Classe_gruppo_mis', 'Classe_Misuratore', 'Classe_misuratore', 'Cl_Mis', 'Cl_mis'),
          candidate.classe_misuratore_fornitura,
          candidate.classe
        ))
        const gasCategoria = findGasCategoria(getFirstValue(
          getObjectValue(rcuTech, 'Cat_uso'),
          supply.categoria_uso_fornitura,
          supply.destinazione_uso,
          candidate.categoria_uso_fornitura,
          candidate.destinazione_uso
        ))
        form.supply.MATRICOLA = getFirstValue(supply.matricola, getObjectValue(rcuTech, 'Matr_mis', 'Matr_conv'), candidate.matricola_contatore_fornitura)
        form.supply.CONSUMO_ANNUO = getFirstValue(supply.consumo_annuo, getObjectValue(rcuTech, 'Prel_annuo_prev'), candidate.consumo_annuo_fornitura)
        form.supply.CLASSE_MISURATORE = gasClasse || getDefaultGasClasse()
        form.supply.DESTINAZIONE_USO = gasCategoria || getDefaultGasCategoria()
        form.supply.POTENZA_TERMICA = getFirstValue(getObjectValue(rcuTech, 'Pot_tot_inst', 'Pot_max_ric'), form.supply.CLASSE_MISURATORE?.portata_termica)
      } else {
        form.supply.MATRICOLA = getFirstValue(getObjectValue(rcuTech, 'Mat_Misuratore_Att', 'Mat_Misuratore_Pot', 'Mat_Misuratore_Rea'), candidate.matricola_contatore_fornitura)
        form.supply.CONSUMO_ANNUO = getFirstValue(getObjectValue(rcuTech, 'Consumo'), candidate.consumo_annuo_fornitura)
      }

      return form
    }

    const openAssociationDialog = async (item, index) => {
      const match = getSelectedMatch(item, index)
      if (!match) return

      associationContext.value = { item, index, match }
      associationForm.value = buildAssociationForm(item, match)
      supportsPecBypass.value = false
      lastSearchedRappresentanteCodfis.value = ''
      associationErrors.value = {}
      associationActiveStep.value = 0
      associationDialogVisible.value = true
      syncAssociationCities()
      await syncAssociationSdiOptions()
      await syncAssociationClientFromBackend()
      if (associationForm.value.agente) {
        loadAssociationOfferte()
      }
    }

    const loadAssociationOfferte = async () => {
      if (!associationForm.value.agente) {
        offerte.value = []
        return
      }

      loadingOfferte.value = true
      await service.getOfferteAgent(associationForm.value.agente)
        .then((res) => {
          offerte.value = normalizeList(res.items)
          const options = associationOfferteOptions.value
          if (!options.some((offerta) => String(offerta.id) === String(associationForm.value.supply.OFFERTA))) {
            associationForm.value.supply.OFFERTA = options.length === 1 ? options[0].id : ''
          }
        })
        .finally(() => {
          loadingOfferte.value = false
          syncAssociationUso()
        })
    }

    const onAssociationAgentChange = () => {
      associationForm.value.supply.OFFERTA = ''
      loadAssociationOfferte()
    }

    const onAssociationTypeClientChange = async (typeClient) => {
      associationForm.value.typeClient = typeClient
      supportsPecBypass.value = false
      lastSearchedRappresentanteCodfis.value = ''
      associationForm.value.contatto.bypass_pec = { value: false, reason: '' }
      clearAssociationErrors(['typeClient', 'rappresentanteCodfis', 'rappresentanteName', 'contattoEmail', 'contattoPec', 'contattoBypassPecReason', 'contattoCellulare', 'contattoToponimo', 'contattoIndirizzo', 'contattoCivico', 'contattoCap', 'contattoProvincia', 'contattoComune'])
      if (typeClient === 2) {
        associationForm.value.rappresentante = null
      } else {
        ensureAssociationRappresentante()
      }
      await syncAssociationSdiOptions()
      await syncAssociationClientFromBackend()
      syncAssociationUso()
    }

    const onAssociationBypassPecChange = (value) => {
      if (!canUseAssociationPecBypass.value) {
        associationForm.value.contatto.bypass_pec = { value: false, reason: '' }
        return
      }

      if (value) {
        associationForm.value.contatto.pec = ''
        clearAssociationErrors(['contattoPec'])
      } else {
        associationForm.value.contatto.bypass_pec.reason = ''
        clearAssociationErrors(['contattoBypassPecReason'])
      }
    }

    const searchAssociationRappresentante = async () => {
      ensureAssociationRappresentante()
      const cf = normalizeRappresentanteCodfis(associationForm.value.rappresentante.codfis)
      associationForm.value.rappresentante.codfis = cf
      if (!isValidRappresentanteCodfis(cf)) {
        associationErrors.value = { ...associationErrors.value, rappresentanteCodfis: true }
        lastSearchedRappresentanteCodfis.value = ''
        return
      }
      if (loadingRappresentante.value || lastSearchedRappresentanteCodfis.value === cf) return

      lastSearchedRappresentanteCodfis.value = cf
      supportsPecBypass.value = false
      clearAssociationRappresentanteContact()
      associationForm.value.rappresentante = {
        ...getEmptyRappresentante(),
        codfis: cf,
        exist: null
      }
      loadingRappresentante.value = true
      await service.check_exist_rapp({
        id_client: associationForm.value.cliente?.id || 0,
        cod_type_client: associationForm.value.typeClient,
        datas: { cf }
      })
        .then((res) => {
          const data = res.res?.data || {}
          supportsPecBypass.value = data.supports_bypass_pec === true
          if (data.exist && data.rappresentante) {
            associationForm.value.rappresentante = {
              ...getEmptyRappresentante(),
              ...data.rappresentante,
              codfis: data.rappresentante.codfis || data.rappresentante.piva || cf,
              nome: data.rappresentante.nome || data.rappresentante.name || '',
              cognome: data.rappresentante.cognome || '',
              exist: true
            }
            setAssociationContactFromList(data.contatti)
          } else {
            associationForm.value.rappresentante = {
              ...getEmptyRappresentante(),
              codfis: cf,
              exist: false
            }
          }
          syncRappresentanteFormaFromCodfis(associationForm.value.rappresentante.codfis)
          clearAssociationErrors(['rappresentanteCodfis', 'rappresentanteName'])
        })
        .finally(() => {
          loadingRappresentante.value = false
        })
    }


    const onAssociationIbanInput = (value) => {
      if (String(value || '').trim()) return

      associationForm.value.supply.checked_data_iban_equal_to_client = true
      associationForm.value.supply.INTESTAZIONE_IBAN = ''
      associationForm.value.supply.CODICE_FISCALE_PIVA_IBAN = ''
      clearAssociationErrors(['ibanOwner'])
    }

    const isAssociationSpedizioneDisabled = (spedizione) => {
      const selectedOfferta = associationOfferteOptions.value.find((offerta) => String(offerta.id) === String(associationForm.value.supply.OFFERTA))
      const canUseCartaceo = !selectedOfferta || Number(selectedOfferta.can_select_ft_cartacea ?? 1) === 1

      return (spedizione?.name || '').includes('Cartaceo') && !canUseCartaceo
    }

    const syncAssociationUso = () => {
      const options = associationUsoOptions.value
      if (!options.some((uso) => uso.name === associationForm.value.supply.USO)) {
        associationForm.value.supply.USO = options.length === 1 ? options[0].name : ''
      }
    }

    const addRequiredDocument = (name, ref, multiple = false) => {
      if (requiredDocuments.value.some((document) => document.ref === ref)) return

      requiredDocuments.value.push({
        name,
        ref,
        files: [],
        multiple
      })
    }

    const syncAssociationRequiredFields = async () => {
      requiredDocuments.value = []
      associationForm.value.contratto_manuale = true
      addRequiredDocument('Contratto firmato cartaceo', 'ctr')

      if (!associationForm.value.agente || !associationForm.value.supply.POD_PDR) return

      loadingRequiredFields.value = true
      await service.getRequiredFields({
        cod_admin: 0,
        cod_agente: associationForm.value.agente,
        type_client: associationForm.value.typeClient,
        pps: [associationForm.value.supply.POD_PDR],
        cod_cliente: associationForm.value.cliente?.id || 0,
        cod_rapp: associationForm.value.rappresentante?.id || 0,
        pratica: associationForm.value.pratica
      })
        .then((res) => {
          const data = res.res?.data || {}
          canSelectBimestrale.value = Boolean(data.can_select_bimestrale)
          canSelectSpedizione.value = data.can_select_spedizione !== false
          canSelectIva.value = data.can_select_iva !== false
          canSelectDataSwitch.value = data.can_select_data_switch !== false
          canInsertNote.value = data.can_insert_note !== false
          availableSpedizioni.value = normalizeList(data.spedizioni).length ? data.spedizioni : [...defaultSpedizioni]
          availableFrequenze.value = canSelectBimestrale.value ? [{ name: 'Mensile' }, { name: 'Bimestrale' }] : [{ name: 'Mensile' }]

          const requestedSpedizione = data.selectedSpedizione || data.selected_spedizione || associationForm.value.spedizione || 'Mail'
          const enabledSpedizioni = availableSpedizioni.value.filter((spedizione) => !isAssociationSpedizioneDisabled(spedizione))
          associationForm.value.spedizione = availableSpedizioni.value.some((spedizione) => spedizione.name === requestedSpedizione) &&
            !isAssociationSpedizioneDisabled({ name: requestedSpedizione })
            ? requestedSpedizione
            : enabledSpedizioni[0]?.name || 'Mail'

          if (!canSelectBimestrale.value || !availableFrequenze.value.some((frequenza) => frequenza.name === associationForm.value.frequenza)) {
            associationForm.value.frequenza = 'Mensile'
          }

          if (data.need_documento) addRequiredDocument('Documento di identita', 'di')
        })
        .finally(() => {
          loadingRequiredFields.value = false
        })
    }

    const handleAssociationFileUpload = (event, document) => {
      const files = Array.from(event.target.files || [])
      document.files = files
      delete associationErrors.value[document.ref]
      delete associationErrors.value[`type_${document.ref}`]
    }

    const copyClienteToContatto = () => {
      const cliente = associationForm.value.cliente
      const clienteCodComune = cliente.cod_comune
      const clienteNomeComune = cliente.nome_comune

      associationForm.value.contatto = {
        ...associationForm.value.contatto,
        toponimo: cliente.toponimo,
        indirizzo: cliente.indirizzo,
        civico: cliente.civico,
        cap: cliente.cap,
        provincia: cliente.provincia,
        cod_comune: null,
        nome_comune: '',
        email: cliente.email,
        pec: cliente.pec,
        cellulare: cliente.cellulare
      }

      setAssociationCities('contatto', false)
      associationForm.value.contatto.cod_comune = clienteCodComune
      associationForm.value.contatto.nome_comune = clienteNomeComune || opzioni.getNameCityById(clienteCodComune)
      clearAssociationErrors(['contattoProvincia', 'contattoComune'])
    }

    const validateAssociationForm = () => {
      const errors = associationWizardSteps.value.reduce((allErrors, step) => {
        return { ...allErrors, ...getAssociationStepErrors(step.key) }
      }, {})
      associationErrors.value = errors
      return Object.keys(errors).length === 0
    }

    const getContattoPayload = () => {
      const form = associationForm.value
      const contatto = form.contatto || {}
      let payload = contatto

      if (form.typeClient === 2 && !contatto.id && !contatto.indirizzo) {
        payload = {
          ...contatto,
          toponimo: form.cliente.toponimo,
          indirizzo: form.cliente.indirizzo,
          civico: form.cliente.civico,
          cap: form.cliente.cap,
          provincia: form.cliente.provincia,
          cod_comune: form.cliente.cod_comune,
          nome_comune: form.cliente.nome_comune,
          email: form.cliente.email,
          pec: form.cliente.pec,
          cellulare: form.cliente.cellulare
        }
      }

      const bypassPec = canUseAssociationPecBypass.value && Boolean(payload.bypass_pec?.value)
      return {
        ...payload,
        bypass_pec: {
          value: bypassPec,
          reason: bypassPec ? upper(String(payload.bypass_pec?.reason || '').trim()) : ''
        }
      }
    }


    const getRappresentantePayload = () => {
      const rappresentante = associationForm.value.rappresentante || getEmptyRappresentante()
      const name = selectedRappresentanteFormaGiuridica.value === 0
        ? [rappresentante.nome, rappresentante.cognome].filter(Boolean).join(' ').trim()
        : rappresentante.name

      return {
        ...rappresentante,
        name: name || ''
      }
    }
    const buildSaveFormData = () => {
      const form = associationForm.value
      const cliente = {
        ...form.cliente,
        codfis: upper(form.cliente.codfis),
        piva: String(form.cliente.piva || '').trim(),
        codiceDestinatario: form.cliente.codiceDestinatario || '0000000'
      }
      const row = {
        ...form.supply,
        RAGIONE_SOCIALE: form.typeClient === 2 ? `${cliente.nome} ${cliente.cognome}`.trim() : cliente.ragsoc,
        CODICE_FISCALE_PIVA: upper(cliente.codfis || cliente.piva),
        CODICE_SDI: cliente.codiceDestinatario,
        POD_PDR: upper(form.supply.POD_PDR),
        DATA_DECORRENZA_FORMATTED: formatDateForPayload(form.supply.DATA_DECORRENZA),
        PRECHECKOK: true
      }
      if (row.ISTAT_FORNITURA && !row.CITTA_FORNITURA) {
        row.CITTA_FORNITURA = opzioni.getNameCityById(row.ISTAT_FORNITURA)
      }

      if (row.TIPO === 'pod') {
        row.STAGIONALITA = row.STAGIONALITA || 'No'
        row.STAGIONALITA_RICORRENTE = row.STAGIONALITA_RICORRENTE || 'No'
        row.DISALIMENTABILE = row.DISALIMENTABILE || 'Si'
        row.CONNESSIONE = row.CONNESSIONE || 'Permanente'
        row.USO_ENERGIA = row.USO_ENERGIA || 'Ordinaria'
        row.DICHIARAZIONE = row.DICHIARAZIONE || 'Condotta in locazione in uso per:'
        row.DICHIARAZIONE_TEXT = row.DICHIARAZIONE_TEXT || ''
      } else if (row.TIPO === 'pdr') {
        row.CLASSE_MISURATORE = findGasClasse(row.CLASSE_MISURATORE) || getDefaultGasClasse()
        row.DESTINAZIONE_USO = findGasCategoria(row.DESTINAZIONE_USO) || getDefaultGasCategoria()
        row.POTENZA_TERMICA = row.POTENZA_TERMICA || row.CLASSE_MISURATORE?.portata_termica || ''
      }

      const fd = new FormData()
      fd.append('matrice_id', JSON.stringify(form.matrice_id || ''))
      fd.append('data', JSON.stringify([row]))
      fd.append('type', JSON.stringify(associationTypeMatrice.value))
      fd.append('rappresentante', JSON.stringify(getRappresentantePayload()))
      fd.append('is_complete_rapp', JSON.stringify(true))
      fd.append('type_client', JSON.stringify(form.typeClient))
      fd.append('iva', JSON.stringify(form.iva))
      fd.append('contatto', JSON.stringify(getContattoPayload()))
      fd.append('cliente', JSON.stringify(cliente))
      fd.append('spedizione', JSON.stringify(form.spedizione))
      fd.append('contratto_manuale', JSON.stringify(form.contratto_manuale))
      fd.append('pratica', JSON.stringify(form.pratica))
      fd.append('agente', form.agente)
      fd.append('data_switch', JSON.stringify(formatDateForPayload(form.data_switch)))
      fd.append('id_promozione', form.id_promozione || '')
      fd.append('id_cliente_promozione', form.id_cliente_promozione || '')
      fd.append('frequenza', JSON.stringify(form.frequenza))
      fd.append('view', JSON.stringify('pps_not_found_rcu'))
      fd.append('pps_not_found_in_rcu_id', JSON.stringify(getSupplyId(associationContext.value?.item || {}) || ''))
      fd.append('pratica_posa_contatore_id', JSON.stringify(getPraticaPosaContatoreDbId(associationContext.value?.match) || ''))
      fd.append('data_firma', JSON.stringify(formatDateForPayload(form.data_firma)))
      fd.append('note', JSON.stringify(form.note))
      requiredDocuments.value.forEach((document) => {
        document.files.forEach((file) => {
          fd.append(`${document.ref}[]`, file)
        })
      })

      return fd
    }

    const submitAssociation = async () => {
      if (!validateAssociationForm()) {
        const firstInvalidStepIndex = associationWizardSteps.value.findIndex((step) => Object.keys(getAssociationStepErrors(step.key)).length > 0)
        if (firstInvalidStepIndex >= 0) associationActiveStep.value = firstInvalidStepIndex
        return
      }

      savingAssociation.value = true
      loading.value = true

      await service.saveMultisitoData(buildSaveFormData())
        .then((res) => {
          if (res.item?.success) {
            associationDialogVisible.value = false
            loadSuggestions()
          }
        })
        .finally(() => {
          savingAssociation.value = false
          loading.value = false
        })
    }

    watch(associationForm, syncVisibleAssociationErrors, { deep: true })
    watch(() => [associationForm.value.data_switch, associationForm.value.data_firma, associationForm.value.data_creazione_pratica, associationForm.value.supply?.DATA_DECORRENZA], normalizeAssociationDates)

    onMounted(() => {
      loadAgents()
      loadPageData()
    })

    return {
      loading,
      loaded,
      loadingConfigurationStatus,
      configurationStatus,
      items,
      associableItems,
      notAssociableItems,
      totalSuggestions,
      selectedMatches,
      associationDialogVisible,
      associationContext,
      associationForm,
      associationErrors,
      savingAssociation,
      associationActiveStep,
      associationWizardSteps,
      activeAssociationStepKey,
      isLastAssociationStep,
      currentUser,
      isAdmin,
      canUseAssociationPecBypass,
      requiresRappresentantePec,
      needsRappresentanteContactInput,
      typeClientOptions,
      agents,
      offerte,
      province,
      toponimi,
      classeGasOptions,
      categorieGasOptions,
      citiesCliente,
      citiesContatto,
      citiesFornitura,
      loadingAgents,
      loadingOfferte,
      loadingRequiredFields,
      loadingRappresentante,
      rappresentanteFormeGiuridiche,
      selectedRappresentanteFormaGiuridica,
      rappresentanteCodfisMask,
      clienteCodfisMask,
      getAssociationRappresentanteDisplayName,
      canSelectBimestrale,
      canSelectSpedizione,
      canSelectIva,
      canSelectDataSwitch,
      canInsertNote,
      associationDataSwitchMaxDate,
      associationDataFirmaMaxDate,
      availableSpedizioni,
      availableFrequenze,
      requiredDocuments,
      associationTypeMatrice,
      hasAssociationIban,
      associationSdiOptions,
      associationOfferteOptions,
      associationUsoOptions,
      canAssociateRows,
      associationConfigurationMessage,
      loadPageData,
      loadSuggestions,
      getSupplyCode,
      getSupplyId,
      getSupplyKey,
      getRowFields,
      getVisibleSupplyFields,
      getAllMatches,
      getMatchKey,
      getMatchLabel,
      getMatchFields,
      getVisibleMatchFields,
      isMatchSelected,
      isMatchMuted,
      setMatchSelection,
      getSelectedMatch,
      openAssociationDialog,
      canCompleteAssociationStep,
      canOpenAssociationStep,
      openAssociationStep,
      previousAssociationStep,
      nextAssociationStep,
      onAssociationAgentChange,
      onAssociationTypeClientChange,
      onAssociationBypassPecChange,
      onAssociationProvinceChange,
      onAssociationCityChange,
      onAssociationIbanInput,
      searchAssociationRappresentante,
      onRappresentanteFormaGiuridicaChange,
      onRappresentanteCodfisInput,
      isAssociationSpedizioneDisabled,
      handleAssociationFileUpload,
      copyClienteToContatto,
      submitAssociation,
      formatLabel,
      formatValue
    }
  }
}
</script>

<style scoped>
.pps-not-found-page {
  padding: 1.5rem;
}

.page-toolbar {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.page-toolbar h2 {
  color: #2f3b52;
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0 0 .25rem;
}

.page-toolbar p {
  color: #687386;
  margin: 0;
}

.summary-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  margin-bottom: 1rem;
}

.summary-item {
  background: var(--surface-card);
  border-left: 4px solid #05b5a6;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .08);
  padding: 1rem;
}

.summary-item span {
  color: #69758a;
  display: block;
  font-size: .85rem;
  margin-bottom: .35rem;
}

.summary-item strong {
  color: #2f3b52;
  font-size: 1.5rem;
}

.cards-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.without-suggestions-panel {
  background: var(--surface-card);
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .08);
  margin-bottom: 1rem;
  padding: 1rem;
}

.without-suggestions-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: .75rem;
}

.without-suggestions-header h3 {
  color: #2f3b52;
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0;
}

.without-suggestions-header span {
  background: #fff8e6;
  border: 1px solid #fedf89;
  border-radius: 999px;
  color: #93370d;
  font-size: .85rem;
  font-weight: 700;
  padding: .2rem .65rem;
}

.without-suggestions-list {
  display: grid;
  gap: .5rem;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.without-suggestions-row {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e4e7ec;
  border-radius: 5px;
  display: grid;
  gap: .5rem;
  grid-template-columns: minmax(120px, 1fr) auto auto auto;
  min-width: 0;
  padding: .55rem .65rem;
}

.without-suggestions-row strong,
.without-suggestions-row span {
  color: #526070;
  font-size: .82rem;
  min-width: 0;
  overflow-wrap: anywhere;
}

.without-suggestions-row strong {
  color: #1d2939;
}

.association-card {
  min-width: 0;
  overflow: hidden;
  padding: .9rem 1rem 1rem;
  position: relative;
}

.association-card h4 {
  color: #2f3b52;
  margin-bottom: .7rem;
}

.association-layout {
  align-items: stretch;
  display: grid;
  gap: .75rem;
  grid-template-columns: minmax(380px, 480px) 112px minmax(520px, 1fr);
}

.association-column h5,
.association-result h5 {
  color: #344054;
  font-size: .96rem;
  margin: 0 0 .55rem;
}

.association-column {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  min-height: 100%;
  padding: .85rem .9rem;
}

.supply-panel {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.supply-detail-block {
  background: #fff;
  border: 1px solid #12b76a;
  border-radius: 6px;
  box-shadow: 0 0 0 2px rgba(18, 183, 106, .14);
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  padding: .75rem .85rem;
}

.association-result {
  align-self: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.detail-grid {
  display: flex;
  flex-direction: column;
  gap: .42rem;
}

.detail-cell {
  align-items: start;
  background: transparent;
  border: 0;
  display: grid;
  gap: .5rem;
  grid-template-columns: 118px minmax(0, 1fr);
  min-width: 0;
  padding: 0;
}

.detail-cell span {
  color: #667085;
  display: block;
  font-size: .8rem;
  line-height: 1.2;
  text-transform: none;
}

.detail-cell strong {
  color: #1d2939;
  display: block;
  font-size: .94rem;
  line-height: 1.28;
  overflow-wrap: anywhere;
}

.candidate-list {
  display: flex;
  flex-direction: column;
  gap: .55rem;
}

.candidate-block {
  background: #fff;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  padding: .75rem .85rem;
  transition: opacity .15s ease, box-shadow .15s ease, border-color .15s ease;
}
.candidate-selected {
  border-color: #12b76a;
  box-shadow: 0 0 0 2px rgba(18, 183, 106, .14);
}

.candidate-muted {
  opacity: .42;
}

.candidate-select-row {
  align-items: center;
  border-bottom: 1px solid #eef2f6;
  display: flex;
  gap: .55rem;
  justify-content: flex-start;
  margin-bottom: .5rem;
  padding-bottom: .45rem;
}

.associate-button {
  margin-top: .45rem;
  width: 100%;
}

.association-config-message {
  color: #b42318;
  display: block;
  font-size: .72rem;
  line-height: 1.2;
  margin-top: .35rem;
  text-align: center;
}

.match-source {
  cursor: pointer;
  font-weight: 700;
  margin-bottom: 0;
}

.result-box {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: .2rem;
  justify-content: center;
  min-height: 64px;
  padding: .5rem;
  text-align: center;
  width: 100%;
}

.result-box i {
  font-size: 1.1rem;
}
.result-ok {
  background-color: #ecfdf3;
  border-color: #abefc6;
  color: #067647;
}

.result-ko {
  background-color: #fff8e6;
  border-color: #fedf89;
  color: #93370d;
}

.no-match-block {
  background: #fff8e6;
  border: 1px solid #fedf89;
  border-radius: 6px;
  color: #93370d;
  padding: .8rem;
}

.association-dialog label {
  color: #526070;
  display: block;
  font-size: .85rem;
  font-weight: 600;
  margin-bottom: .35rem;
}

.rappresentante-type-selector {
  display: flex;
  justify-content: center;
}

.rappresentante-search-row {
  display: flex;
  justify-content: center;
}

.rappresentante-search-field {
  max-width: 520px;
  width: 100%;
}

.rappresentante-details-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
}

.rappresentante-detail-field {
  max-width: 360px;
  width: 100%;
}

.rappresentante-detail-field-wide {
  max-width: 520px;
}

.association-loading-text {
  display: inline-flex;
  align-items: center;
  color: #175cd3;
  margin-top: .35rem;
}

.rappresentante-found-box {
  align-items: center;
  background: #ecfdf3;
  border: 1px solid #75e0a7;
  border-radius: 6px;
  color: #05603a;
  display: flex;
  flex-direction: column;
  gap: .35rem;
  padding: 1rem;
}

.rappresentante-found-box h4 {
  margin: 0;
}

.association-step-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 1rem;
  padding: 1rem;
}

.association-step-panel h3 {
  color: #2f3b52;
  font-size: 1.15rem;
  font-weight: 600;
  margin-top: 0;
}

.association-wizard {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.wizard-steps {
  display: grid;
  gap: .5rem;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
}

.wizard-step-button {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  color: #475467;
  cursor: pointer;
  display: flex;
  font-weight: 700;
  gap: .5rem;
  justify-content: flex-start;
  min-height: 44px;
  padding: .55rem .7rem;
}

.wizard-step-button span {
  align-items: center;
  background: #e4e7ec;
  border-radius: 50%;
  color: #475467;
  display: inline-flex;
  flex: 0 0 24px;
  height: 24px;
  justify-content: center;
}

.wizard-step-button.active {
  background: #eef8ff;
  border-color: #84caff;
  color: #175cd3;
}

.wizard-step-button.active span {
  background: #1570ef;
  color: #fff;
}

.wizard-step-button.complete:not(.active) {
  border-color: #abefc6;
}

.wizard-step-button:disabled {
  cursor: not-allowed;
  opacity: .45;
}

.type-client-selector {
  display: grid;
  gap: .65rem;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  width: 100%;
}

.type-client-selector :deep(.p-button) {
  justify-content: center;
  min-height: 2.65rem;
  width: 100%;
}

.type-client-selector :deep(.btn-selection:not(.selected)) {
  background: #fff;
  border: 1px solid #7da0b8;
  color: #48657a;
}

.type-client-selector :deep(.btn-selection.selected) {
  background: #108ac7;
  border-color: #108ac7;
  color: #fff;
}

.type-client-selector-invalid {
  border: 1px solid #f87171;
  border-radius: 6px;
  padding: .5rem;
}

.document-section {
  min-height: 120px;
}

.association-document-block {
  border: 0;
  border-radius: 0;
  margin-bottom: .75rem;
  padding-bottom: .75rem;
}

.association-document-block + .association-document-block {
  border-top: 1px solid #e5e7eb;
  padding-top: .75rem;
}

.association-option-card {
  background: #f8fafc;
  border: 1px solid #d9e2ec;
  border-radius: 4px;
  margin: .5rem;
}

.association-required-documents {
  list-style: none;
  margin: 0;
  padding: 0;
  text-align: center;
}

.button-wrapper {
  position: relative;
  text-align: center;
  width: 150px;
}

.button-wrapper span.label {
  background: #00bfff;
  border-radius: 3px;
  box-shadow: 0 10px 13px -7px #00000050, 5px 5px 15px 5px rgba(0, 0, 0, 0);
  color: #fff;
  cursor: pointer;
  display: inline-block;
  font-size: 12px;
  height: 50px !important;
  padding: 10px 0;
  position: relative;
  text-transform: uppercase;
  width: 100%;
  z-index: 0;
}

.button-wrapper .upload-file {
  cursor: pointer;
  display: inline-block;
  height: 50px !important;
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;
}
.section-divider {
  border-top: 1px solid #e5e7eb;
  color: #344054;
  margin-top: .5rem;
  padding-top: 1rem;
}

.document-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.document-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: .85rem;
}

.document-title {
  color: #344054;
  font-weight: 700;
  margin-bottom: .65rem;
}

.upload-file {
  width: 100%;
}

.document-files {
  color: #475467;
  font-size: .82rem;
  margin: .65rem 0 0;
  padding-left: 1.1rem;
}

.p-invalid-card {
  border-color: #f97066;
}

.dialog-summary {
  display: grid;
  gap: .75rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  margin-bottom: 1rem;
}

.dialog-summary div {
  background: #f7f9fb;
  border-left: 4px solid #05b5a6;
  padding: .75rem;
}

.dialog-summary span {
  color: #687386;
  display: block;
  font-size: .8rem;
  margin-bottom: .25rem;
}

.dialog-summary strong {
  color: #2f3b52;
  overflow-wrap: anywhere;
}

.checkbox-field {
  align-items: center;
  display: flex;
  gap: .5rem;
  padding-top: 2rem;
}

.checkbox-field label {
  margin-bottom: 0;
}

.association-radio-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: .85rem;
}

.association-radio-box .radio-box-title {
  color: #344054;
  font-weight: 700;
  margin-bottom: .65rem;
  text-align: center;
}

.radio-line {
  align-items: center;
  display: flex;
  gap: .5rem;
  margin: .55rem 0;
}

.radio-line label {
  margin-bottom: 0;
}

@media (max-width: 1100px) {
  .association-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .association-result {
  align-self: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

  .candidates-panel {
    order: 3;
  }

  .without-suggestions-row {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
  }

  .candidate-select-row {
  align-items: center;
  border-bottom: 1px solid #eef2f6;
  display: flex;
  gap: .55rem;
  justify-content: flex-start;
  margin-bottom: .5rem;
  padding-bottom: .45rem;
}}

@media (max-width: 640px) {
  .pps-not-found-page {
    padding: 1rem;
  }

  .toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .association-card {
    padding: .9rem;
  }

  .detail-cell {
    gap: .15rem;
    grid-template-columns: minmax(0, 1fr);
  }

  .without-suggestions-list {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
